import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Activity,
  Play,
  CheckCircle2,
  Clock,
  Wrench,
  AlertTriangle,
  Search,
  ScanBarcode,
  PlusCircle,
  Layers,
  LayoutGrid,
  Table as TableIcon,
  UserCheck,
  Zap,
  ArrowRight,
  ShieldCheck,
  ShieldAlert,
  RotateCcw,
  RefreshCw,
  Box,
  FileText,
  StopCircle,
  Flame,
  Film,
  Package,
  Paintbrush,
  Maximize2,
  Minimize2,
  Timer,
  Tag,
  Check,
  Truck,
  Monitor,
  Calendar,
  CalendarClock,
  History,
  ClipboardList,
  Camera,
  Upload,
  Image as ImageIcon,
  Trash2,
  Gauge,
  Workflow,
  PackageCheck,
  ClipboardCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useSearch } from '@tanstack/react-router';
import { ShopfloorScannerModal } from '@/components/factory/ShopfloorScannerModal';
import { uploadFile } from '@/lib/uploadFile';
import { getUploadUrl, cn, formatNumber, validateUploadFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from '@/lib/utils';
import { QCFinalInspectionModal } from '@/components/quality/QCFinalInspectionModal';
import { WipPalletTagPrintModal, WipPalletTagData } from './components/WipPalletTagPrintModal';

import { toVnDateString, toVnDateTimeLocal } from "@apv-erp/status";
// Machine Categories Definition for Easy Filtering
type MachineCategory = 'ALL' | 'OFFSET' | 'LAMINATION' | 'DIECUT' | 'GLUING' | 'FINISHING';
type ViewMode = 'TERMINAL' | 'GRID' | 'TABLE';
type DateFilterType = 'ALL' | 'TODAY' | 'TOMORROW' | 'YESTERDAY' | 'THIS_WEEK';

export function formatLocalDatetimeInput(dateInput: Date | number | string): string {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(d.getTime())) return '';
  return toVnDateTimeLocal(d);
}

export default function ShopfloorTouchTerminalPage() {
  const queryClient = useQueryClient();
  const searchParams = useSearch({ strict: false }) as any;

  // View Mode: Terminal Kiosk vs Cards Matrix vs Dispatch Table
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const m = urlParams?.get('mode') || searchParams?.mode;
      if (m === 'grid') return 'GRID';
      if (m === 'table') return 'TABLE';
      if (m === 'terminal') return 'TERMINAL';
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/mes')) {
        return 'GRID';
      }
    } catch {}
    return 'TABLE';
  });

  // Selected Machine for Single Machine Touch Terminal Mode
  const [selectedMachineId, setSelectedMachineId] = useState<string>(() => {
    try {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const mId = urlParams?.get('machineId') || searchParams?.machineId;
      if (mId) return mId;
      return localStorage.getItem('apv_shopfloor_selected_machine') || '';
    } catch {
      return searchParams?.machineId || '';
    }
  });

  // Quick Date Filter: TODAY (hôm nay), TOMORROW (ngày mai), YESTERDAY (hôm qua), THIS_WEEK (tuần này), ALL (tất cả)
  const [dateFilter, setDateFilter] = useState<DateFilterType>(() => {
    if (searchParams?.date === 'today') return 'TODAY';
    if (searchParams?.date === 'tomorrow') return 'TOMORROW';
    if (searchParams?.date === 'yesterday') return 'YESTERDAY';
    if (searchParams?.date === 'week') return 'THIS_WEEK';
    return 'ALL';
  });

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<MachineCategory>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'RUNNING' | 'IDLE' | 'DOWN'>('ALL');
  const [machineFilter, setMachineFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>(() => searchParams?.orderId || searchParams?.code || '');

  // Highlighted Machine ID (from Barcode scan)
  const [highlightedMachineId, setHighlightedMachineId] = useState<string | null>(() => searchParams?.machineId || null);

  // History modal state for reviewing logs of a step
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    step: any | null;
  }>({
    isOpen: false,
    step: null,
  });

  // Scanner modal state
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  // Stopwatch for downtime tracking
  const [downtimeSeconds, setDowntimeSeconds] = useState<number>(0);
  const [downtimeModalOpen, setDowntimeModalOpen] = useState(false);

  // Phase 123: TPM Shift Checklist & Emergency Incident States
  const [shiftChecklistModalOpen, setShiftChecklistModalOpen] = useState(false);
  const [emergencyIncidentModalOpen, setEmergencyIncidentModalOpen] = useState(false);
  const [checklistData, setChecklistData] = useState<Record<string, boolean>>({
    pneumatic: true,
    lubrication: true,
    rollerBlade: true,
    safetyInterlock: true,
    alignmentGuide: true,
  });
  const [checklistNotes, setChecklistNotes] = useState('');
  const [emergencyType, setEmergencyType] = useState('MECHANICAL');
  const [emergencyReason, setEmergencyReason] = useState('');
  const [emergencySeverity, setEmergencySeverity] = useState<'CRITICAL' | 'WARNING'>('CRITICAL');

  // Modals state
  const [logOutputModal, setLogOutputModal] = useState<{
    isOpen: boolean;
    machine: any | null;
    step: any | null;
    qtyOkAdd: number;
    qtyNgAdd: number;
    operatorId: string;
    operatorName: string;
    runStartTime: string;
    runEndTime: string;
    notes: string;
    imageUrls: string[];
    isUploadingImage?: boolean;
    activeNumpadField: 'OK' | 'NG';
    selectedDefectReason?: string;
  }>({
    isOpen: false,
    machine: null,
    step: null,
    qtyOkAdd: 0,
    qtyNgAdd: 0,
    operatorId: '',
    operatorName: '',
    runStartTime: '',
    runEndTime: '',
    notes: '',
    imageUrls: [],
    isUploadingImage: false,
    activeNumpadField: 'OK',
    selectedDefectReason: '',
  });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: 'START' | 'START_SETUP' | 'FINISH';
    machine: any | null;
    step: any | null;
    operatorName: string;
  }>({
    isOpen: false,
    action: 'START',
    machine: null,
    step: null,
    operatorName: '',
  });

  // Phase 120: Modal In Tem Pallet Bán Thành Phẩm ISO
  const [wipPalletModal, setWipPalletModal] = useState<{
    isOpen: boolean;
    palletData: WipPalletTagData | null;
  }>({
    isOpen: false,
    palletData: null,
  });

  // Phase 120: Nhận bài tức thì qua Barcode / QR Scan
  const [terminalScanQuery, setTerminalScanQuery] = useState('');

  const [assignJobModal, setAssignJobModal] = useState<{
    isOpen: boolean;
    machine: any | null;
    searchFilter: string;
  }>({
    isOpen: false,
    machine: null,
    searchFilter: '',
  });

  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    machine: any | null;
    targetStatus: 'IDLE' | 'RUNNING' | 'DOWN' | 'MAINTENANCE';
    reason: string;
  }>({
    isOpen: false,
    machine: null,
    targetStatus: 'DOWN',
    reason: '',
  });

  const [wipModal, setWipModal] = useState<{
    isOpen: boolean;
    step: any | null;
    machine: any | null;
    quantity: number;
    palletCode: string;
    notes: string;
  }>({
    isOpen: false,
    step: null,
    machine: null,
    quantity: 0,
    palletCode: '',
    notes: '',
  });

  const [qcModal, setQcModal] = useState<{
    isOpen: boolean;
    step: any | null;
    machine: any | null;
    inspectorName: string;
    status: 'PASSED' | 'FAILED';
    notes: string;
  }>({
    isOpen: false,
    step: null,
    machine: null,
    inspectorName: 'KCS Ca 1: Nguyễn Văn An',
    status: 'PASSED',
    notes: '',
  });

  // Phase 86: FQC Inspection Modal from Terminal
  const [fqcModalPoId, setFqcModalPoId] = useState<string | null>(null);

  // Red-Team UX Resilience: Hỗ trợ bàn phím số vật lý (0-9, Backspace) khi Modal Numpad mở
  useEffect(() => {
    if (!logOutputModal.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        const digit = e.key;
        setLogOutputModal((prev) => {
          const f = prev.activeNumpadField || 'OK';
          if (f === 'OK') {
            const currentVal = prev.qtyOkAdd || 0;
            const nextVal = Math.min(parseInt(`${currentVal}${digit}`, 10) || 0, 10_000_000);
            return { ...prev, qtyOkAdd: nextVal };
          } else {
            const currentVal = prev.qtyNgAdd || 0;
            const nextVal = Math.min(parseInt(`${currentVal}${digit}`, 10) || 0, 10_000_000);
            return { ...prev, qtyNgAdd: nextVal };
          }
        });
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setLogOutputModal((prev) => {
          const f = prev.activeNumpadField || 'OK';
          if (f === 'OK') {
            return { ...prev, qtyOkAdd: Math.floor((prev.qtyOkAdd || 0) / 10) };
          } else {
            return { ...prev, qtyNgAdd: Math.floor((prev.qtyNgAdd || 0) / 10) };
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [logOutputModal.isOpen]);

  // 1. Fetch Real Shopfloor Matrix & Process Schedule Data
  const { data: matrixData, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['shopfloor', 'shopfloorMatrix', dateFilter, machineFilter, categoryFilter, statusFilter, searchQuery],
    queryFn: () =>
      trpcClient.shopfloor.getShopfloorMatrix.query({
        dateFilter,
        machineId: machineFilter !== 'ALL' ? machineFilter : undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: searchQuery.trim() || undefined,
      }),
    refetchInterval: 15000,
  });

  // Query historical production logs for the selected step in historyModal
  const { data: stepLogsData, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['shopfloor', 'stepLogs', historyModal.step?.id],
    queryFn: () => trpcClient.shopfloor.getStepLogs.query({ stepId: historyModal.step?.id }),
    enabled: Boolean(historyModal.isOpen && historyModal.step?.id),
  });

  // Auto-select machine: Prioritize URL param (id or machine code) -> then current/localStorage -> fallback to first
  useEffect(() => {
    if (matrixData?.machines && matrixData.machines.length > 0) {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const urlMachineId = urlParams?.get('machineId') || searchParams?.machineId;

      if (urlMachineId) {
        const found = matrixData.machines.find(
          (m: any) => m.id === urlMachineId || m.machineId === urlMachineId
        );
        if (found) {
          if (selectedMachineId !== found.id) {
            setSelectedMachineId(found.id);
            try {
              localStorage.setItem('apv_shopfloor_selected_machine', found.id);
            } catch {}
          }
          return;
        }
      }

      if (!selectedMachineId || !matrixData.machines.some((m: any) => m.id === selectedMachineId)) {
        const first = matrixData.machines[0];
        setSelectedMachineId(first.id);
        try {
          localStorage.setItem('apv_shopfloor_selected_machine', first.id);
        } catch {}
      }
    }
  }, [matrixData?.machines, selectedMachineId]);

  // Fullscreen Kiosk Mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Stopwatch timer when machine is DOWN
  useEffect(() => {
    const currentMachine = matrixData?.machines?.find((m: any) => m.id === selectedMachineId);
    if (currentMachine?.status === 'DOWN') {
      const interval = setInterval(() => {
        setDowntimeSeconds((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setDowntimeSeconds(0);
    }
  }, [matrixData?.machines, selectedMachineId]);

  // Timers: Setup vs Run Stopwatch
  const [setupElapsedSeconds, setSetupElapsedSeconds] = useState(0);
  const [runElapsedSeconds, setRunElapsedSeconds] = useState(0);

  useEffect(() => {
    const currentMachine = matrixData?.machines?.find((m: any) => m.id === selectedMachineId);
    const currentStep = currentMachine?.activeStep;
    if (!currentStep) {
      setSetupElapsedSeconds(0);
      setRunElapsedSeconds(0);
      return;
    }

    const updateTimers = () => {
      const now = Date.now();
      if (currentStep.currentPhase === 'SETUP' && currentStep.setupStartTime) {
        const start = new Date(currentStep.setupStartTime).getTime();
        setSetupElapsedSeconds(Math.max(0, Math.floor((now - start) / 1000)));
      } else {
        setSetupElapsedSeconds(0);
      }

      if (currentStep.currentPhase === 'RUNNING' && currentStep.runStartTime) {
        const start = new Date(currentStep.runStartTime).getTime();
        setRunElapsedSeconds(Math.max(0, Math.floor((now - start) / 1000)));
      } else {
        setRunElapsedSeconds(0);
      }
    };

    updateTimers();
    const interval = setInterval(updateTimers, 1000);
    return () => clearInterval(interval);
  }, [matrixData?.machines, selectedMachineId]);

  const formatDuration = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const hours = Math.floor(mins / 60);
    if (hours > 0) {
      return `${hours}h ${mins % 60}p ${secs < 10 ? '0' : ''}${secs}s`;
    }
    return `${mins}p ${secs < 10 ? '0' : ''}${secs}s`;
  };

  // 2. Mutations
  const startExecutionMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId: string; operatorName?: string; operatorId?: string }) =>
      trpcClient.shopfloor.startStepExecution.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã bắt đầu chạy bài in trên máy!');
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi bắt đầu: ${err.message}`);
    },
  });

  // Phase 120: Bắt đầu căn chỉnh máy (Setup)
  const startSetupMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId: string; operatorName?: string; operatorId?: string }) =>
      trpcClient.shopfloor.startStepSetup.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('⚡ Đã bắt đầu giai đoạn Căn chỉnh máy (Setup)!');
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi bắt đầu căn chỉnh: ${err.message}`);
    },
  });

  // Phase 120: Chuyển từ Căn chỉnh sang Chạy sản xuất chính thức (Run)
  const transitionSetupToRunMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId: string; setupMinutes?: number }) =>
      trpcClient.shopfloor.transitionSetupToRun.mutate(args),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success(`▶️ Đã chốt ${data.actualSetupTime || 0}p căn chỉnh và chuyển sang chạy sản xuất chính thức!`);
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi chuyển sang chạy sản xuất: ${err.message}`);
    },
  });

  // Phase 120: Tạo phiếu chuyển giao BTP & Tem Pallet ISO
  const createWipPalletTagMutation = useMutation({
    mutationFn: (args: { stepId: string; productionOrderId: string; quantity: number; palletCode?: string; notes?: string }) =>
      trpcClient.shopfloor.createWipPalletTag.mutate(args),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      if (data?.palletData) {
        setWipPalletModal({
          isOpen: true,
          palletData: data.palletData,
        });
      }
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi sinh tem pallet: ${err.message}`);
    },
  });

  const finishExecutionMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId?: string; finalQtyOk?: number; finalQtyNg?: number; notes?: string }) =>
      trpcClient.shopfloor.finishStepExecution.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      const completedStep = confirmModal.step;
      const completedPoId = completedStep?.productionOrderId;
      
      // Phase 120: Tự động tạo tem pallet BTP khi hoàn thành bài chạy
      if (completedStep && completedPoId) {
        createWipPalletTagMutation.mutate({
          stepId: completedStep.id,
          productionOrderId: completedPoId,
          quantity: completedStep.actualQtyOk || completedStep.standardQuantity || 1000,
        });
      }

      if (completedPoId) {
        toast.success('Đã hoàn thành bài in, đưa máy về IDLE!', {
          action: {
            label: '📋 Nghiệm Thu FQC',
            onClick: () => setFqcModalPoId(completedPoId),
          },
        });
      } else {
        toast.success('Đã hoàn thành bài in, đưa máy về IDLE và đón bài tiếp theo!');
      }
      setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi hoàn thành: ${err.message}`);
    },
  });

  const logOutputMutation = useMutation({
    mutationFn: (args: {
      stepId: string;
      qtyOk: number;
      qtyNg?: number;
      machineId?: string;
      operatorId: string;
      operatorName?: string;
      runStartTime?: string;
      runEndTime?: string;
      notes?: string;
      imageUrls?: string[];
    }) => trpcClient.shopfloor.logProductionOutput.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã lưu sản lượng vào Nhật ký sản xuất và cập nhật tiến độ!');
      setLogOutputModal((prev) => ({ ...prev, isOpen: false, imageUrls: [] }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi báo sản lượng: ${err.message}`);
    },
  });

  const checkInStepMutation = useMutation({
    mutationFn: (args: {
      stepId: string;
      machineId?: string;
      operatorId: string;
      operatorName?: string;
      startTime?: string;
    }) => trpcClient.shopfloor.checkInStep.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('⚡ Đã Check-in vào ca thành công! Công đoạn chuyển sang trạng thái Đang Chạy (RUNNING).');
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi check-in: ${err.message}`);
    },
  });

  const quickLogOutputMutation = useMutation({
    mutationFn: (args: { stepId: string; qtyOkAdd: number; qtyNgAdd?: number; machineId?: string; operatorName?: string; notes?: string }) =>
      trpcClient.shopfloor.quickLogOutput.mutate(args),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success(`Đã ghi nhận sản lượng: +${data.step?.actualQtyOk?.toLocaleString()} tờ. Tiến độ: ${data.progressPercent}%`);
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi ghi nhận sản lượng: ${err.message}`);
    },
  });

  const reportAndonDowntimeMutation = useMutation({
    mutationFn: (args: { machineId: string; stepId?: string; reason: string; notes?: string; reporterName?: string }) =>
      trpcClient.shopfloor.reportAndonDowntime.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.error('Đã báo sự cố dừng máy Andon và ghi nhật ký máy!');
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi báo dừng máy: ${err.message}`);
    },
  });

  const resumeMachineOperationMutation = useMutation({
    mutationFn: (args: { machineId: string; stepId?: string; durationMinutes?: number }) =>
      trpcClient.shopfloor.resumeMachineOperation.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã phục hồi máy khỏi sự cố, tiếp tục chạy sản xuất!');
      setDowntimeSeconds(0);
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi phục hồi máy: ${err.message}`);
    },
  });

  const assignStepMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId: string; sequenceNumber?: number }) =>
      trpcClient.shopfloor.assignStepToMachine.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã gán công đoạn vào máy!');
      setAssignJobModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi gán máy: ${err.message}`);
    },
  });

  const assignOperatorMutation = useMutation({
    mutationFn: (args: { machineId: string; operatorId?: string; operatorName: string }) =>
      trpcClient.shopfloor.assignOperatorToMachine.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã cập nhật thợ máy phụ trách!');
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi gán thợ máy: ${err.message}`);
    },
  });

  const reportStatusMutation = useMutation({
    mutationFn: (args: { machineId: string; status: 'IDLE' | 'RUNNING' | 'DOWN' | 'MAINTENANCE'; reason?: string }) =>
      trpcClient.shopfloor.reportMachineStatus.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã cập nhật trạng thái máy!');
      setStatusModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi đổi trạng thái máy: ${err.message}`);
    },
  });

  // Phase 123: TPM Mutations
  const submitShiftChecklistMutation = useMutation({
    mutationFn: (args: { machineId: string; shift?: string; checklist: Record<string, boolean>; passed: boolean; notes?: string }) =>
      trpcClient.maintenance.submitShiftChecklist.mutate(args),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      if (res.passed !== false) {
        toast.success(res.message);
      } else {
        toast.warning(res.message);
      }
      setShiftChecklistModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(`Lỗi gửi phiếu kiểm tra: ${err.message}`);
    },
  });

  const reportEmergencyIncidentMutation = useMutation({
    mutationFn: (args: { machineId: string; issueType: string; reason: string; severity: 'CRITICAL' | 'WARNING'; notes?: string }) =>
      trpcClient.maintenance.reportEmergencyIncident.mutate(args),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      toast.error(res.message, { duration: 5000 });
      setEmergencyIncidentModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(`Lỗi báo sự cố: ${err.message}`);
    },
  });

  const handoverWipMutation = useMutation({
    mutationFn: (args: { stepId: string; quantity: number; palletCode?: string; notes?: string; senderName?: string }) =>
      trpcClient.shopfloor.handoverWip.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      toast.success('Đã lập phiếu bàn giao BTP sang công đoạn kế tiếp!');
      setWipModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi bàn giao BTP: ${err.message}`);
    },
  });

  const submitFirstPieceQcMutation = useMutation({
    mutationFn: (args: { stepId: string; machineId: string; status: 'PASSED' | 'FAILED'; inspectorName?: string; notes?: string }) =>
      trpcClient.shopfloor.submitFirstPieceQc.mutate(args),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
      if (res.status === 'PASSED') {
        toast.success('Nghiệm thu bài mẫu ĐẠT! Máy được phép chạy tốc độ cao.');
      } else {
        toast.error('Bài mẫu KHÔNG ĐẠT! Máy đã tạm dừng để cân chỉnh lại.');
      }
      setQcModal((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi nghiệm thu QC: ${err.message}`);
    },
  });

  // 3. Category matching helper
  const isMachineInCategory = (m: any, cat: MachineCategory) => {
    if (cat === 'ALL') return true;
    const type = m.type || '';
    const name = (m.name || '').toLowerCase();
    if (cat === 'OFFSET') {
      return type === 'OFFSET_PRINTER' || type === 'FLEXO_PRINTER' || name.includes('in');
    }
    if (cat === 'LAMINATION') {
      return type === 'LAMINATOR' || type === 'COATER' || name.includes('màng') || name.includes('cán');
    }
    if (cat === 'DIECUT') {
      return type === 'DIE_CUTTER' || type === 'HOT_FOIL' || name.includes('bế') || name.includes('nhũ') || name.includes('ép');
    }
    if (cat === 'GLUING') {
      return type === 'FOLDER_GLUER' || type === 'WINDOW_PATCHER' || name.includes('dán');
    }
    if (cat === 'FINISHING') {
      return type === 'PAPER_CUTTER' || type === 'PACKING' || name.includes('xén') || name.includes('cắt') || name.includes('gói');
    }
    return true;
  };

  // 4. Filtered machines list
  const filteredMachines = useMemo(() => {
    const list = matrixData?.machines || [];
    return list.filter((m: any) => {
      if (!isMachineInCategory(m, categoryFilter)) return false;

      if (statusFilter !== 'ALL') {
        if (statusFilter === 'RUNNING' && m.status !== 'RUNNING') return false;
        if (statusFilter === 'IDLE' && m.status !== 'IDLE') return false;
        if (statusFilter === 'DOWN' && (m.status !== 'DOWN' && m.status !== 'MAINTENANCE')) return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const mId = (m.machineId || '').toLowerCase();
        const mName = (m.name || '').toLowerCase();
        const poCode = (m.activeStep?.poCode || '').toLowerCase();
        const prodName = (m.activeStep?.productName || '').toLowerCase();
        const custName = (m.activeStep?.customerName || '').toLowerCase();
        const soCode = (m.activeStep?.salesOrderCode || '').toLowerCase();
        const opName = (m.operator?.name || '').toLowerCase();

        const matchThis =
          mId.includes(q) ||
          mName.includes(q) ||
          poCode.includes(q) ||
          prodName.includes(q) ||
          custName.includes(q) ||
          soCode.includes(q) ||
          opName.includes(q);

        const matchQueue = (m.queue || []).some((qs: any) =>
          (qs.poCode || '').toLowerCase().includes(q) ||
          (qs.productName || '').toLowerCase().includes(q)
        );

        if (!matchThis && !matchQueue) return false;
      }

      return true;
    });
  }, [matrixData?.machines, categoryFilter, statusFilter, searchQuery]);

  // 5. Filtered Process Steps List (Bảng Kế Hoạch & Điều Độ Công Đoạn)
  const displaySteps = useMemo(() => {
    const steps = matrixData?.steps || [];
    return steps.filter((s: any) => {
      if (categoryFilter !== 'ALL') {
        const name = (s.name || '').toLowerCase();
        const mType = s.machine?.type || '';
        const mName = (s.machine?.name || '').toLowerCase();
        if (categoryFilter === 'OFFSET') {
          if (!mType.includes('OFFSET') && !mType.includes('FLEXO') && !name.includes('in') && !mName.includes('in')) return false;
        } else if (categoryFilter === 'LAMINATION') {
          if (!mType.includes('LAMINAT') && !mType.includes('COAT') && !name.includes('màng') && !name.includes('cán') && !mName.includes('màng')) return false;
        } else if (categoryFilter === 'DIECUT') {
          if (!mType.includes('DIE_CUT') && !mType.includes('HOT_FOIL') && !name.includes('bế') && !name.includes('nhũ') && !name.includes('ép')) return false;
        } else if (categoryFilter === 'GLUING') {
          if (!mType.includes('GLUER') && !mType.includes('PATCHER') && !name.includes('dán')) return false;
        } else if (categoryFilter === 'FINISHING') {
          if (!mType.includes('CUTTER') && !mType.includes('PACKING') && !name.includes('xén') && !name.includes('cắt') && !name.includes('gói') && !name.includes('đóng')) return false;
        }
      }

      if (machineFilter !== 'ALL') {
        if (s.machineId !== machineFilter) return false;
      }

      if (statusFilter !== 'ALL') {
        if (statusFilter === 'RUNNING' && s.status !== 'RUNNING') return false;
        if (statusFilter === 'IDLE' && (s.status === 'RUNNING' || s.status === 'COMPLETED')) return false;
        if (statusFilter === 'DOWN' && s.machine?.status !== 'DOWN' && s.status !== 'PAUSED') return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const poCode = (s.poCode || '').toLowerCase();
        const pName = (s.productName || '').toLowerCase();
        const cName = (s.customerName || '').toLowerCase();
        const sName = (s.name || '').toLowerCase();
        const mName = (s.machine?.name || '').toLowerCase();
        const mId = (s.machine?.machineId || '').toLowerCase();
        if (!poCode.includes(q) && !pName.includes(q) && !cName.includes(q) && !sName.includes(q) && !mName.includes(q) && !mId.includes(q)) return false;
      }

      return true;
    });
  }, [matrixData?.steps, categoryFilter, machineFilter, statusFilter, searchQuery]);

  // Count steps per process category tag
  const categoryCounts = useMemo(() => {
    const steps = matrixData?.steps || [];
    const counts: Record<string, number> = {
      ALL: steps.length,
      OFFSET: 0,
      LAMINATION: 0,
      DIECUT: 0,
      GLUING: 0,
      FINISHING: 0,
    };
    steps.forEach((s: any) => {
      const name = (s.name || '').toLowerCase();
      const mType = s.machine?.type || '';
      const mName = (s.machine?.name || '').toLowerCase();
      if (mType.includes('OFFSET') || mType.includes('FLEXO') || name.includes('in') || mName.includes('in')) {
        counts.OFFSET++;
      } else if (mType.includes('LAMINAT') || mType.includes('COAT') || name.includes('màng') || name.includes('cán') || mName.includes('màng')) {
        counts.LAMINATION++;
      } else if (mType.includes('DIE_CUT') || mType.includes('HOT_FOIL') || name.includes('bế') || name.includes('nhũ') || name.includes('ép')) {
        counts.DIECUT++;
      } else if (mType.includes('GLUER') || mType.includes('PATCHER') || name.includes('dán')) {
        counts.GLUING++;
      } else if (mType.includes('CUTTER') || mType.includes('PACKING') || name.includes('xén') || name.includes('cắt') || name.includes('gói') || name.includes('đóng')) {
        counts.FINISHING++;
      }
    });
    return counts;
  }, [matrixData?.steps]);

  // Count steps per date filter tag
  const dateCounts = useMemo(() => {
    const steps = matrixData?.steps || [];
    const now = new Date();
    const todayStr = toVnDateString(now);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = toVnDateString(tomorrow);

    const counts: Record<string, number> = {
      ALL: steps.length,
      TODAY: 0,
      TOMORROW: 0,
      THIS_WEEK: 0,
    };

    steps.forEach((s: any) => {
      const rawDate = s.plannedStartDate || s.plannedEndDate;
      let dateStr = '';
      if (rawDate) {
        try {
          const d = new Date(rawDate);
          if (!isNaN(d.getTime())) {
            dateStr = toVnDateString(d);
          }
        } catch {}
      }
      if (dateStr === todayStr) {
        counts.TODAY++;
      } else if (dateStr === tomorrowStr) {
        counts.TOMORROW++;
      }
      if (dateStr) {
        const d = new Date(dateStr);
        const diffDays = Math.abs((d.getTime() - now.getTime()) / (1000 * 3600 * 24));
        if (diffDays <= 7) {
          counts.THIS_WEEK++;
        }
      }
    });

    if (dateFilter === 'TODAY' && counts.TODAY === 0) counts.TODAY = steps.length;
    if (dateFilter === 'TOMORROW' && counts.TOMORROW === 0) counts.TOMORROW = steps.length;
    if (dateFilter === 'THIS_WEEK' && counts.THIS_WEEK === 0) counts.THIS_WEEK = steps.length;

    return counts;
  }, [matrixData?.steps, dateFilter]);

  // Selected Machine Record for Touch Terminal Mode
  const activeTerminalMachine = useMemo(() => {
    if (!selectedMachineId) return matrixData?.machines?.[0] || null;
    return matrixData?.machines?.find((m: any) => m.id === selectedMachineId) || matrixData?.machines?.[0] || null;
  }, [matrixData?.machines, selectedMachineId]);

  const summary = matrixData?.summary || {
    totalMachines: 0,
    runningCount: 0,
    idleCount: 0,
    downCount: 0,
    totalActiveJobs: 0,
    unassignedCount: 0,
  };

  const operators = matrixData?.operators || [];
  const unassignedSteps = matrixData?.availableUnassignedSteps || [];

  // 6 Quick Loss Time Reasons for Touch Buttons
  const QUICK_LOSS_REASONS = [
    { label: 'Gá Bài / Chỉnh Cử', code: 'SETUP', icon: Wrench, color: 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300' },
    { label: 'Chờ Nhiệt Độ', code: 'WAIT_TEMP', icon: Flame, color: 'text-orange-700 bg-orange-50 hover:bg-orange-100 border-orange-300' },
    { label: 'Chờ Màng Nhiệt', code: 'WAIT_FILM', icon: Film, color: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-300' },
    { label: 'Chờ Cấp Giấy', code: 'WAIT_PAPER', icon: Package, color: 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-300' },
    { label: 'Múc Mực / Rửa Máy', code: 'WASH_INK', icon: Paintbrush, color: 'text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-300' },
    { label: 'Kẹt Dao / Lỗi Kỹ Thuật', code: 'BLADE_JAM', icon: AlertTriangle, color: 'text-rose-700 bg-rose-50 hover:bg-rose-100 border-rose-300' },
  ];

  // 5 Dây Chuyền Sản Xuất / Phân Xưởng Nhà Máy Bao Bì
  const PROCESS_SECTIONS = [
    {
      id: 'OFFSET',
      name: 'DÂY CHUYỀN 1: PHÂN XƯỞNG IN (OFFSET & FLEXO)',
      icon: Paintbrush,
      color: 'text-indigo-700 bg-indigo-50 border-indigo-200',
    },
    {
      id: 'LAMINATION',
      name: 'DÂY CHUYỀN 2: PHÂN XƯỞNG CÁN MÀNG NHIỆT & PHỦ UV',
      icon: Film,
      color: 'text-blue-700 bg-blue-50 border-blue-200',
    },
    {
      id: 'DIECUT',
      name: 'DÂY CHUYỀN 3: PHÂN XƯỞNG BẾ TỰ ĐỘNG & BẾ TRỤC',
      icon: Box,
      color: 'text-purple-700 bg-purple-50 border-purple-200',
    },
    {
      id: 'GLUING',
      name: 'DÂY CHUYỀN 4: PHÂN XƯỞNG DÁN HỘP & DÁN CỬA SỔ',
      icon: Package,
      color: 'text-amber-700 bg-amber-50 border-amber-200',
    },
    {
      id: 'FINISHING',
      name: 'DÂY CHUYỀN 5: PHÂN XƯỞNG HOÀN THIỆN & ĐÓNG GÓI',
      icon: Truck,
      color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    },
  ];

  const openLogOutputModal = (machine: any, step: any) => {
    const defaultOperatorId = step?.assignedOperator?.id || machine?.operator?.id || (operators[0]?.id || '');
    const defaultOperatorName = step?.assignedOperator?.name || machine?.operator?.name || (operators[0]?.name || 'Thợ máy');
    const now = new Date();
    const nowString = formatLocalDatetimeInput(now);
    // Giờ bắt đầu: giờ bấm chạy thật (nhật ký đang chạy); giờ kế hoạch chỉ dùng khi đã qua — giờ kế hoạch còn ở phía sau
    // làm báo sản lượng ngay sau khi chạy bị chặn "Giờ kết thúc phải sau giờ bắt đầu" (đi chuỗi bán hàng 03/11/2026)
    const scheduledStart = step?.scheduledStartTime ? new Date(step.scheduledStartTime).getTime() : null;
    const startString = step?.runStartTime
      ? formatLocalDatetimeInput(step.runStartTime)
      : scheduledStart && scheduledStart <= now.getTime()
        ? formatLocalDatetimeInput(scheduledStart)
        : formatLocalDatetimeInput(now.getTime() - 60 * 60 * 1000);

    setLogOutputModal({
      isOpen: true,
      machine,
      step,
      qtyOkAdd: 0,
      qtyNgAdd: 0,
      operatorId: defaultOperatorId,
      operatorName: defaultOperatorName,
      runStartTime: startString,
      runEndTime: nowString,
      notes: '',
      imageUrls: [],
      isUploadingImage: false,
      activeNumpadField: 'OK',
      selectedDefectReason: '',
    });
  };

  // Phase 120: Xử lý Barcode Scanner khớp bài tức thì <= 0.1s
  const handleTerminalScan = (val: string) => {
    setTerminalScanQuery(val);
    const cleaned = val.trim().toLowerCase();
    if (!cleaned || !activeTerminalMachine) return;
    
    const matchedJob = activeTerminalMachine.queue?.find((job: any) =>
      job.poCode?.toLowerCase() === cleaned ||
      job.id?.toLowerCase() === cleaned ||
      job.salesOrderCode?.toLowerCase() === cleaned ||
      (job.productionOrderId && job.productionOrderId.toLowerCase() === cleaned)
    );

    if (matchedJob) {
      toast.success(`⚡ Quét thành công: Khớp bài ${matchedJob.poCode} trong hàng đợi!`, { duration: 3000 });
      setConfirmModal({
        isOpen: true,
        action: 'START_SETUP',
        machine: activeTerminalMachine,
        step: matchedJob,
        operatorName: activeTerminalMachine.operator?.name || 'Thợ máy',
      });
      setTerminalScanQuery('');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setLogOutputModal((prev) => ({ ...prev, isUploadingImage: true }));
    try {
      const uploadedUrls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        validateUploadFile(file, {
          maxSize: MAX_MEDIA_IMAGE_SIZE,
          label: 'Ảnh đính kèm',
        });
        const result = await uploadFile(file);
        if (result?.url) {
          uploadedUrls.push(result.url);
        }
      }
      setLogOutputModal((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...uploadedUrls],
        isUploadingImage: false,
      }));
      toast.success(`Đã tải lên ${uploadedUrls.length} ảnh đính kèm`);
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Lỗi khi tải ảnh'));
      setLogOutputModal((prev) => ({ ...prev, isUploadingImage: false }));
    } finally {
      e.target.value = '';
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setLogOutputModal((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  return (
    <div className="p-3 sm:p-5 space-y-3 max-w-[1720px] mx-auto min-h-screen bg-slate-50/50">
      {/* 1. Header Toolbar Tinh Gọn 1 Hàng Duy Nhất: Context + Mode Switcher + Live KPI Pills + Quick Actions */}
      <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-2.5">
        {/* Left: Context Badge & Mode Switcher */}
        <div className="flex items-center gap-2.5 overflow-x-auto scrollbar-none">
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="px-2.5 py-1 rounded-lg text-xs font-black uppercase tracking-tight bg-primary/10 text-primary border border-primary/20 flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              {viewMode === 'GRID' && 'MA TRẬN XƯỞNG MES'}
              {viewMode === 'TABLE' && 'BẢNG KẾ HOẠCH CA'}
              {viewMode === 'TERMINAL' && 'TRẠM CẢM ỨNG MÁY'}
            </span>
            <Badge variant="outline" className="text-[11px] font-bold font-mono px-2 py-0.5 bg-background border-border shrink-0">
              {dateFilter === 'TODAY' ? '📅 Ca Hôm Nay' : dateFilter === 'TOMORROW' ? '📅 Ngày Mai' : dateFilter === 'THIS_WEEK' ? '📅 Tuần Này' : '📅 Toàn Bộ Kế Hoạch'}
            </Badge>
          </div>

          <div className="flex items-center bg-muted/80 p-0.5 rounded-lg border border-border shrink-0">
            <Button
              variant={viewMode === 'TERMINAL' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('TERMINAL')}
              className="h-7 px-2.5 text-xs font-bold gap-1"
              title="Góc nhìn Thợ vận hành máy trên Tablet cảm ứng"
            >
              <Monitor className="h-3 w-3" />
              Trạm Tablet (Thợ)
            </Button>
            <Button
              variant={viewMode === 'GRID' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('GRID')}
              className="h-7 px-2.5 text-xs font-bold gap-1"
              title="Góc nhìn Quản lý xưởng - Tổng quan trạng thái toàn xưởng"
            >
              <LayoutGrid className="h-3 w-3" />
              Ma Trận Xưởng (Quản Lý)
            </Button>
            <Button
              variant={viewMode === 'TABLE' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('TABLE')}
              className="h-7 px-2.5 text-xs font-bold gap-1"
              title="Góc nhìn Tổ trưởng công đoạn - Kế hoạch & Điều độ"
            >
              <TableIcon className="h-3 w-3" />
              Bảng Kế Hoạch
            </Button>
          </div>
        </div>

        {/* Right: Live Health Pills & Essential Actions */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
          {/* Live KPI Compact Pill */}
          <div className="flex items-center gap-1.5 bg-muted/60 border border-border/80 rounded-lg px-2.5 py-1 text-xs font-mono shrink-0">
            <span className="text-emerald-700 font-black flex items-center gap-1">
              <Play className="h-3 w-3 fill-emerald-600 text-emerald-600" />
              {summary.runningCount}/{summary.totalMachines} chạy
            </span>
            <span className="text-border">|</span>
            <span className="text-amber-700 font-bold">{summary.idleCount} rảnh</span>
            <span className="text-border">|</span>
            <span className="text-rose-700 font-bold">{summary.downCount || 0} dừng</span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setAssignJobModal({
                isOpen: true,
                machine: activeTerminalMachine,
                searchFilter: '',
              })
            }
            className="h-7 px-2.5 gap-1 font-bold text-xs text-indigo-700 border-indigo-300 bg-indigo-50/50 hover:bg-indigo-100/50 shrink-0"
          >
            <PlusCircle className="h-3.5 w-3.5 text-indigo-600" />
            Lệnh chờ ({summary.unassignedCount})
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsScannerOpen(true)}
            className="h-7 px-2.5 gap-1 font-bold text-xs bg-primary/5 text-primary border-primary/30 hover:bg-primary/10 shrink-0"
          >
            <ScanBarcode className="h-3.5 w-3.5" />
            Quét LSX
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="h-7 px-2 text-xs font-semibold text-muted-foreground hover:text-foreground shrink-0"
            title="Làm mới dữ liệu"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* 2. CHẾ ĐỘ 1: TRẠM MÁY CẢM ỨNG ĐƠN (TOUCH TERMINAL KIOSK) */}
      {viewMode === 'TERMINAL' && activeTerminalMachine && (
        <div className="space-y-4">
          {/* Machine Control Bar: Chọn máy sản xuất & Thợ vận hành */}
          <div className="bg-card border-2 border-primary/30 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="space-y-1">
                <span className="text-[11px] font-bold uppercase text-muted-foreground">Trạm Máy Đang Chọn:</span>
                <Select
                  value={selectedMachineId || activeTerminalMachine.id}
                  onValueChange={(mId) => {
                    setSelectedMachineId(mId);
                    try {
                      localStorage.setItem('apv_shopfloor_selected_machine', mId);
                    } catch {}
                  }}
                >
                  <SelectTrigger className="h-10 text-sm font-black min-w-[280px] bg-background border-2 border-primary/40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(matrixData?.machines || []).map((m: any) => (
                      <SelectItem key={m.id} value={m.id} className="text-xs font-bold">
                        <span className="font-mono text-primary font-black mr-2">[{m.machineId}]</span>
                        {m.name}
                        {m.status === 'RUNNING' && <span className="ml-2 text-emerald-600 text-[11px]">🟢 Đang chạy</span>}
                        {m.status === 'DOWN' && <span className="ml-2 text-rose-600 text-[11px]">🔴 Dừng máy</span>}
                        {m.status === 'IDLE' && <span className="ml-2 text-amber-600 text-[11px]">🟡 Rảnh</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <span className="text-[11px] font-bold uppercase text-muted-foreground">Thợ Phụ Trách Máy:</span>
                <Select
                  value={activeTerminalMachine.operator?.id || 'none'}
                  onValueChange={(uId) => {
                    if (uId === 'none') return;
                    const u = operators.find((op: any) => op.id === uId);
                    if (u) {
                      assignOperatorMutation.mutate({
                        machineId: activeTerminalMachine.id,
                        operatorId: u.id,
                        operatorName: u.name,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-10 text-xs font-semibold min-w-[200px] bg-background">
                    <UserCheck className="h-3.5 w-3.5 text-primary mr-1.5" />
                    <span className="truncate">{activeTerminalMachine.operator?.name || 'Gán thợ máy'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled className="text-xs">-- Chọn thợ máy --</SelectItem>
                    {operators.map((u: any) => (
                      <SelectItem key={u.id} value={u.id} className="text-xs">
                        {u.name} ({u.department || u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Trạng Thái Máy Live & Đèn Tháp ANDON */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-[11px] font-bold text-muted-foreground uppercase">Trạng Thái Máy</div>
                <div className="flex items-center gap-1.5 mt-0.5 justify-end">
                  {activeTerminalMachine.status === 'RUNNING' ? (
                    <Badge className="bg-emerald-600 text-white font-black text-xs px-2.5 py-1 gap-1.5 shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      ĐANG CHẠY SẢN XUẤT
                    </Badge>
                  ) : activeTerminalMachine.status === 'DOWN' ? (
                    <Badge className="bg-rose-600 text-white font-black text-xs px-2.5 py-1 gap-1.5 shadow-sm">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      SỰ CỐ DỪNG MÁY
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white font-black text-xs px-2.5 py-1 gap-1.5 shadow-sm">
                      <Clock className="h-3.5 w-3.5" />
                      MÁY ĐANG TRỐNG / RẢNH
                    </Badge>
                  )}
                </div>
              </div>

              {/* Nút Kiểm Tra Đầu Ca (Shift Checklist) */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShiftChecklistModalOpen(true)}
                className="h-10 text-xs font-bold gap-1.5 border-indigo-300 bg-indigo-50/70 text-indigo-700 hover:bg-indigo-100"
              >
                <ClipboardCheck className="h-4 w-4 text-indigo-600" />
                <span>Kiểm Tra Đầu Ca</span>
              </Button>

              {/* Nút Báo Sự Cố Khẩn Cấp / Khôi Phục Vận Hành */}
              <Button
                variant={activeTerminalMachine.status === 'DOWN' ? 'outline' : 'destructive'}
                size="sm"
                onClick={() => {
                  if (activeTerminalMachine.status === 'DOWN') {
                    resumeMachineOperationMutation.mutate({
                      machineId: activeTerminalMachine.id,
                      stepId: activeTerminalMachine.activeStep?.id,
                    });
                  } else {
                    setEmergencyIncidentModalOpen(true);
                  }
                }}
                className={`h-10 text-xs font-bold gap-1.5 shadow-sm ${
                  activeTerminalMachine.status === 'DOWN'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-black'
                    : 'bg-rose-600 hover:bg-rose-700 text-white font-black animate-pulse'
                }`}
              >
                {activeTerminalMachine.status === 'DOWN' ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span>Mở Khóa Vận Hành</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-white" />
                    <span>BÁO SỰ CỐ</span>
                  </>
                )}
              </Button>

              {/* Nút Toàn Màn Hình Kiosk Mode */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
                className="h-10 text-xs font-bold gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
                title="Bật/Tắt chế độ toàn màn hình cho Kiosk Tablet"
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Thu Nhỏ</span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Toàn Màn Hình</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* HƯỚNG DẪN QUY TRÌNH 3 BƯỚC THAO TÁC TẠI MÁY */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className="text-xs font-black uppercase tracking-tight text-primary flex items-center gap-1.5 shrink-0">
              <Zap className="w-4 h-4 text-primary" />
              HƯỚNG DẪN QUY TRÌNH 3 BƯỚC THAO TÁC TẠI MÁY
            </span>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-xs">
              <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary font-bold">
                1. Nhận bài & Bấm BẮT ĐẦU CHẠY
              </span>
              <span className="text-muted-foreground/40">➔</span>
              <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-bold">
                2. Trong ca: Bấm BÁO SẢN LƯỢNG kèm ảnh thực tế
              </span>
              <span className="text-muted-foreground/40">➔</span>
              <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-800 font-bold">
                3. Xong sản lượng: Bấm KẾT THÚC BÀI CHẠY
              </span>
            </div>
          </div>

          {/* Banner Cảnh Báo Nếu Máy Đang Dừng Sự Cố (Andon Downtime) */}
          {activeTerminalMachine.status === 'DOWN' && (
            <div className="p-4 bg-rose-500/10 border-2 border-rose-500 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-rose-600 text-white rounded-lg">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-black text-rose-700 uppercase">
                    CẢNH BÁO: MÁY ĐANG DỪNG DO SỰ CỐ / DOWNTIME
                  </h3>
                  <p className="text-xs text-rose-600 mt-0.5">
                    Thời gian dừng: <b className="font-mono text-sm">{Math.floor(downtimeSeconds / 60)}p {downtimeSeconds % 60}s</b> &bull; Đã ghi nhận bản ghi MachineLog vào hệ thống.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                onClick={() =>
                  resumeMachineOperationMutation.mutate({
                    machineId: activeTerminalMachine.id,
                    stepId: activeTerminalMachine.activeStep?.id,
                  })
                }
                className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs gap-1.5 shadow-md"
              >
                <Check className="h-4 w-4" />
                ĐÃ KHẮC PHỤC XONG - TIẾP TỤC CHẠY
              </Button>
            </div>
          )}

          {/* KỊCH BẢN 1: MÁY ĐANG CÓ BÀI CHẠY (ACTIVE JOB) - GIAO DIỆN KIOSK CÔNG NGHIỆP RÕ RÀNG */}
          {activeTerminalMachine.activeStep ? (
            <div className="space-y-4">
              {/* Phase 120: Two-Stage Chu Trình Vận Hành 2 Giai Đoạn (Setup vs Run) */}
              {activeTerminalMachine.activeStep.currentPhase === 'SETUP' ? (
                <div className="bg-amber-500/15 border-2 border-amber-500 rounded-xl p-3.5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-amber-500 text-white rounded-lg animate-pulse shrink-0">
                      <Timer className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-xs font-black uppercase text-amber-800 flex items-center gap-2">
                        <span>GIAI ĐOẠN 1: ĐANG CĂN CHỈNH MÁY (SETUP / MAKE-READY)</span>
                        <Badge className="bg-amber-600 text-white text-[10px] font-mono px-2 py-0">ĐANG ĐẾM GIỜ</Badge>
                      </div>
                      <div className="text-xs text-amber-700 mt-0.5">
                        Thời gian căn chỉnh thực tế: <strong className="font-mono text-sm text-foreground">{formatDuration(setupElapsedSeconds)}</strong> &bull; Định mức: ~{activeTerminalMachine.activeStep.standardSetupTime || 30} phút
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() =>
                      transitionSetupToRunMutation.mutate({
                        stepId: activeTerminalMachine.activeStep.id,
                        machineId: activeTerminalMachine.id,
                      })
                    }
                    disabled={transitionSetupToRunMutation.isPending}
                    className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs gap-1.5 shadow-md shrink-0"
                  >
                    <Play className="h-4 w-4 fill-white" />
                    ▶️ BẮT ĐẦU CHẠY CHÍNH THỨC (RUN)
                  </Button>
                </div>
              ) : (
                <div className="bg-emerald-500/10 border-2 border-emerald-500/40 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded-full bg-emerald-500 animate-ping shrink-0" />
                    <div>
                      <span className="text-xs font-black uppercase text-emerald-800">
                        GIAI ĐOẠN 2: ĐANG CHẠY SẢN XUẤT CHÍNH THỨC (RUNNING)
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        Thời gian chạy máy: <strong className="font-mono text-foreground">{formatDuration(runElapsedSeconds)}</strong>
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    Mục tiêu ca: <strong className="text-foreground">{activeTerminalMachine.activeStep.standardQuantity?.toLocaleString()} tờ</strong>
                  </div>
                </div>
              )}

              {/* 1-2-3 LINEAR GUIDED FLOW BANNER (QUY TRÌNH 3 BƯỚC VẬN HÀNH CA) */}
              <div className="bg-card border border-border rounded-xl px-4 py-2.5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Trình Tự Ca:</span>
                  <div className="flex items-center gap-1.5 text-xs font-bold">
                    <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 flex items-center gap-1 font-mono">
                      <Check className="w-3.5 h-3.5" /> 1. Đã Nhận &amp; Bắt Đầu
                    </span>
                    <span className="text-muted-foreground">➔</span>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white flex items-center gap-1 shadow-2xs animate-pulse font-mono">
                      <FileText className="w-3.5 h-3.5" /> 2. Báo Sản Lượng
                    </span>
                    <span className="text-muted-foreground">➔</span>
                    <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 flex items-center gap-1 font-mono">
                      <CheckCircle2 className="w-3.5 h-3.5" /> 3. Kết Thúc / FQC
                    </span>
                  </div>
                </div>
                {/* 4 Trụ Cột Sẵn Sàng (4-Pillar Readiness Badges) */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className={cn("text-[10px] font-bold py-0.5 px-2 gap-1", activeTerminalMachine.activeStep.isPaperReady !== false ? "text-emerald-700 border-emerald-400 bg-emerald-50/60" : "text-amber-700 border-amber-400 bg-amber-50/60")}>
                    📄 Giấy {activeTerminalMachine.activeStep.isPaperReady !== false ? 'OK' : 'Chờ'}
                  </Badge>
                  <Badge variant="outline" className={cn("text-[10px] font-bold py-0.5 px-2 gap-1", activeTerminalMachine.activeStep.isPlateReady !== false ? "text-emerald-700 border-emerald-400 bg-emerald-50/60" : "text-amber-700 border-amber-400 bg-amber-50/60")}>
                    🖨️ Kẽm {activeTerminalMachine.activeStep.isPlateReady !== false ? 'OK' : 'Chờ'}
                  </Badge>
                  <Badge variant="outline" className={cn("text-[10px] font-bold py-0.5 px-2 gap-1", activeTerminalMachine.activeStep.isDieReady !== false ? "text-emerald-700 border-emerald-400 bg-emerald-50/60" : "text-amber-700 border-amber-400 bg-amber-50/60")}>
                    ✂️ Khuôn {activeTerminalMachine.activeStep.isDieReady !== false ? 'OK' : 'Chờ'}
                  </Badge>
                  <Badge variant="outline" className={cn("text-[10px] font-bold py-0.5 px-2 gap-1", activeTerminalMachine.activeStep.isAuxReady !== false ? "text-emerald-700 border-emerald-400 bg-emerald-50/60" : "text-amber-700 border-amber-400 bg-amber-50/60")}>
                    🧪 Phụ trợ {activeTerminalMachine.activeStep.isAuxReady !== false ? 'OK' : 'Chờ'}
                  </Badge>
                </div>
              </div>

              {/* 1. THẺ BÀI ĐANG CHẠY (HEADER BÀI CHẠY) */}
              <div className="bg-card border-2 border-primary/30 rounded-2xl p-4 sm:p-5 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-border/60">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xl sm:text-2xl font-black text-primary tracking-tight">
                        {activeTerminalMachine.activeStep.poCode}
                      </span>
                      {activeTerminalMachine.activeStep.salesOrderCode && (
                        <Badge variant="outline" className="font-mono text-xs text-muted-foreground">
                          ĐH: {activeTerminalMachine.activeStep.salesOrderCode}
                        </Badge>
                      )}
                      <Badge className="bg-primary text-primary-foreground font-bold text-xs px-2.5 py-0.5">
                        {activeTerminalMachine.activeStep.name}
                      </Badge>
                    </div>
                    <h2 className="text-lg sm:text-xl font-black text-foreground">
                      {activeTerminalMachine.activeStep.productName}
                    </h2>
                    <div className="text-xs text-muted-foreground">
                      Khách hàng: <span className="font-semibold text-foreground">{activeTerminalMachine.activeStep.customerName}</span>
                      {activeTerminalMachine.activeStep.dimensions && activeTerminalMachine.activeStep.dimensions !== '—' && (
                        <span> &bull; Quy cách: <strong className="font-mono text-foreground">{activeTerminalMachine.activeStep.dimensions}</strong></span>
                      )}
                    </div>
                  </div>

                  {/* Thông tin khổ & UPS / Vị trí gá */}
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap bg-muted/40 p-2.5 rounded-xl border border-border/60">
                    <div className="text-xs">
                      <span className="text-muted-foreground text-[11px] block">Khổ In / Cắt:</span>
                      <strong className="font-mono text-foreground text-sm">{activeTerminalMachine.activeStep.sheetSize || '—'}</strong>
                    </div>
                    <div className="h-6 w-px bg-border/60" />
                    <div className="text-xs">
                      <span className="text-muted-foreground text-[11px] block">Bình Bản (UPS):</span>
                      <strong className="font-mono text-foreground text-sm">{activeTerminalMachine.activeStep.outs} con/tờ</strong>
                    </div>
                    {/* Khuôn hoặc Kẽm */}
                    {activeTerminalMachine.activeStep.tooling && (
                      <>
                        <div className="h-6 w-px bg-border/60" />
                        <div className="text-xs">
                          <span className="text-muted-foreground text-[11px] block">
                            {activeTerminalMachine.activeStep.tooling.type === 'PLATE' ? 'Kẽm CTP:' : 'Khuôn Gá Máy:'}
                          </span>
                          <span className="font-mono font-bold text-primary text-xs">
                            {activeTerminalMachine.activeStep.tooling.code}
                          </span>
                        </div>
                        {activeTerminalMachine.activeStep.tooling.location && (
                          <>
                            <div className="h-6 w-px bg-border/60" />
                            <div className="text-xs">
                              <span className="text-muted-foreground text-[11px] block">📍 Vị Trí Lưu Kho:</span>
                              <Badge variant="outline" className="font-mono text-xs font-bold bg-amber-50 text-amber-900 border-amber-300">
                                {activeTerminalMachine.activeStep.tooling.location}
                              </Badge>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Dòng hiển thị Lô Vật Tư Sàn Xưởng đã cấp (Material Staging Lots) */}
                {activeTerminalMachine.activeStep.stagedMaterials && activeTerminalMachine.activeStep.stagedMaterials.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-bold text-muted-foreground uppercase text-[11px] flex items-center gap-1">
                      <Box className="h-3.5 w-3.5 text-primary" /> Lô Vật Tư Cấp Phát:
                    </span>
                    {activeTerminalMachine.activeStep.stagedMaterials.map((sm: any) => (
                      <Badge key={sm.id} variant="secondary" className="font-mono text-xs py-1 px-2.5 gap-1.5 border">
                        <span className="font-bold text-foreground">{sm.materialName}</span>
                        <span className="text-indigo-600 font-black">Lô: {sm.lotNumber}</span>
                        <span className="text-muted-foreground">({sm.quantity?.toLocaleString()} {sm.unit})</span>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* 2. HERO KPI SẢN LƯỢNG SIÊU TO - CHUẨN CÔNG NGHIỆP NHÌN XA 1.5M */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* HERO CARD 1: ĐẦU VÀO CA MÁY (INPUT) */}
                <div className="bg-card border-2 border-indigo-300 rounded-2xl p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase text-indigo-700 tracking-wider flex items-center gap-1.5">
                      <Box className="h-4 w-4" /> ĐẦU VÀO CA MÁY (INPUT)
                    </span>
                    {activeTerminalMachine.activeStep.inputSourceType === 'ACTUAL_PREV_STEP' ? (
                      <Badge className="bg-emerald-600 text-white font-bold text-xs py-0.5 px-2.5 shadow-2xs">
                        ✓ Nhận từ [{activeTerminalMachine.activeStep.prevStepName || 'C.Đoạn Trước'}]
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs font-semibold text-muted-foreground">
                        📊 Kế hoạch ban đầu (F0)
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-baseline gap-2">
                    <div className="font-mono text-4xl sm:text-5xl font-black text-foreground tracking-tight">
                      {(activeTerminalMachine.activeStep.inputQty || activeTerminalMachine.activeStep.standardQuantity)?.toLocaleString()}
                    </div>
                    <span className="text-sm font-bold text-muted-foreground">tờ</span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/60">
                    <span>Hao hụt cho phép: <strong className="text-foreground font-mono">~{activeTerminalMachine.activeStep.expectedWasteQty || 30} tờ</strong></span>
                    <span>Mục tiêu đạt: <strong className="text-foreground font-mono">{(activeTerminalMachine.activeStep.standardQuantity)?.toLocaleString()} tờ</strong></span>
                  </div>
                </div>

                {/* HERO CARD 2: SẢN LƯỢNG ĐÃ CHẠY (OUTPUT) */}
                <div className="bg-card border-2 border-emerald-500/60 rounded-2xl p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase text-emerald-700 tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> SẢN LƯỢNG ĐÃ CHẠY (OUTPUT)
                    </span>
                    <span className="font-mono font-black text-sm text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-300">
                      {activeTerminalMachine.activeStep.progressPercent}% Hoàn Thành
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 items-baseline">
                    <div>
                      <span className="text-[11px] font-bold text-muted-foreground block uppercase">ĐẠT CHUẨN (OK):</span>
                      <div className="font-mono text-4xl sm:text-5xl font-black text-emerald-600 tracking-tight">
                        {activeTerminalMachine.activeStep.actualQtyOk.toLocaleString()}
                        <span className="text-xs font-bold text-muted-foreground ml-1">tờ</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] font-bold text-muted-foreground block uppercase">PHẾ PHẨM (NG):</span>
                      <div className="font-mono text-3xl sm:text-4xl font-black text-rose-600 tracking-tight">
                        {activeTerminalMachine.activeStep.actualQtyNg.toLocaleString()}
                        <span className="text-xs font-bold text-muted-foreground ml-1">tờ</span>
                      </div>
                    </div>
                  </div>

                  {/* Thanh Tiến Độ To Bản */}
                  <div className="h-3 w-full bg-muted rounded-full overflow-hidden p-0.5 border border-border">
                    <div
                      className="h-full bg-emerald-600 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, activeTerminalMachine.activeStep.progressPercent)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* 3. CỤM NÚT BẤM CẢM ỨNG CỰC LỚN (INDUSTRIAL TOUCH BAR $\ge 56$PX) */}
              <div className="bg-card border-2 border-primary/20 rounded-2xl p-4 sm:p-5 shadow-sm space-y-3.5">
                <div className="text-[11px] font-black uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Zap className="h-4 w-4 text-amber-500 fill-amber-500" />
                  Ghi Nhận Sản Lượng 1-Chạm (Pallet Quick Counter)
                </div>

                {/* Hàng 1: Nút cộng nhanh OK lớn (Chiều cao 56px) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
                  <Button
                    type="button"
                    onClick={() =>
                      quickLogOutputMutation.mutate({
                        stepId: activeTerminalMachine.activeStep.id,
                        qtyOkAdd: 500,
                        machineId: activeTerminalMachine.id,
                        operatorName: activeTerminalMachine.operator?.name,
                      })
                    }
                    className="h-14 text-base font-black bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-xs"
                  >
                    +500 tờ
                  </Button>
                  <Button
                    type="button"
                    onClick={() =>
                      quickLogOutputMutation.mutate({
                        stepId: activeTerminalMachine.activeStep.id,
                        qtyOkAdd: 1000,
                        machineId: activeTerminalMachine.id,
                        operatorName: activeTerminalMachine.operator?.name,
                      })
                    }
                    className="h-14 text-base font-black bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shadow-xs"
                  >
                    +1,000 tờ
                  </Button>
                  <Button
                    type="button"
                    onClick={() =>
                      quickLogOutputMutation.mutate({
                        stepId: activeTerminalMachine.activeStep.id,
                        qtyOkAdd: 2000,
                        machineId: activeTerminalMachine.id,
                        operatorName: activeTerminalMachine.operator?.name,
                      })
                    }
                    className="h-14 text-base font-black bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-xs"
                  >
                    +2,000 tờ
                  </Button>
                  <Button
                    type="button"
                    onClick={() =>
                      quickLogOutputMutation.mutate({
                        stepId: activeTerminalMachine.activeStep.id,
                        qtyOkAdd: 5000,
                        machineId: activeTerminalMachine.id,
                        operatorName: activeTerminalMachine.operator?.name,
                      })
                    }
                    className="h-14 text-base font-black bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-xs"
                  >
                    +5,000 tờ
                  </Button>
                </div>

                {/* Hàng 2: Nút Hành Động Trọng Tâm (Chiều cao 56px) */}
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 sm:gap-3 pt-1">
                  {activeTerminalMachine.activeStep.currentPhase === 'SETUP' ? (
                    <>
                      {/* Khi đang Căn chỉnh (SETUP): Nút chuyển sang chạy RUN là nút ưu tiên số 1 */}
                      <Button
                        type="button"
                        onClick={() =>
                          transitionSetupToRunMutation.mutate({
                            stepId: activeTerminalMachine.activeStep.id,
                            machineId: activeTerminalMachine.id,
                          })
                        }
                        disabled={transitionSetupToRunMutation.isPending}
                        className="sm:col-span-5 h-14 text-sm sm:text-base font-black bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md border-2 border-emerald-500 gap-2"
                      >
                        <Play className="h-5 w-5 fill-white" />
                        ▶️ CHUYỂN SANG CHẠY MÁY (RUN)
                      </Button>

                      {/* Nút Báo ca kèm ảnh */}
                      <Button
                        type="button"
                        onClick={() => openLogOutputModal(activeTerminalMachine, activeTerminalMachine.activeStep)}
                        className="sm:col-span-4 h-14 text-xs sm:text-sm font-black bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-md border-2 border-indigo-500 gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        📋 BÁO SẢN LƯỢNG / THỬ MÁY
                      </Button>

                      {/* Nút Hoàn thành bài */}
                      <Button
                        type="button"
                        onClick={() =>
                          setConfirmModal({
                            isOpen: true,
                            action: 'FINISH',
                            machine: activeTerminalMachine,
                            step: activeTerminalMachine.activeStep,
                            operatorName: activeTerminalMachine.operator?.name || 'Thợ máy',
                          })
                        }
                        className="sm:col-span-3 h-14 font-black text-xs sm:text-sm bg-slate-900 hover:bg-slate-800 text-white gap-1.5 shadow-md rounded-xl"
                      >
                        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                        🏁 KẾT THÚC BÀI CHẠY
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Nút Báo ca kèm ảnh */}
                      <Button
                        type="button"
                        onClick={() => openLogOutputModal(activeTerminalMachine, activeTerminalMachine.activeStep)}
                        className="sm:col-span-6 h-14 text-base font-black bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md border-2 border-emerald-500 gap-2"
                      >
                        <FileText className="h-5 w-5" />
                        📋 BƯỚC 2: BÁO SẢN LƯỢNG KÈM ẢNH
                      </Button>

                      {/* Nút ghi phế */}
                      <div className="sm:col-span-3 flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            quickLogOutputMutation.mutate({
                              stepId: activeTerminalMachine.activeStep.id,
                              qtyOkAdd: 0,
                              qtyNgAdd: 10,
                              machineId: activeTerminalMachine.id,
                              operatorName: activeTerminalMachine.operator?.name,
                            })
                          }
                          className="flex-1 h-14 text-xs font-black text-rose-600 border-2 border-rose-300 hover:bg-rose-50 rounded-xl"
                        >
                          +10 Phế
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            quickLogOutputMutation.mutate({
                              stepId: activeTerminalMachine.activeStep.id,
                              qtyOkAdd: 0,
                              qtyNgAdd: 50,
                              machineId: activeTerminalMachine.id,
                              operatorName: activeTerminalMachine.operator?.name,
                            })
                          }
                          className="flex-1 h-14 text-xs font-black text-rose-700 border-2 border-rose-400 hover:bg-rose-100 rounded-xl"
                        >
                          +50 Phế
                        </Button>
                      </div>

                      {/* Nút Hoàn thành bài */}
                      <Button
                        type="button"
                        onClick={() =>
                          setConfirmModal({
                            isOpen: true,
                            action: 'FINISH',
                            machine: activeTerminalMachine,
                            step: activeTerminalMachine.activeStep,
                            operatorName: activeTerminalMachine.operator?.name || 'Thợ máy',
                          })
                        }
                        className="sm:col-span-3 h-14 font-black text-xs sm:text-sm bg-slate-900 hover:bg-slate-800 text-white gap-1.5 shadow-md rounded-xl"
                      >
                        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                        🏁 BƯỚC 3: KẾT THÚC BÀI CHẠY
                      </Button>
                    </>
                  )}
                </div>

                {/* Hàng 3: Các nút phụ trợ (Gọn gàng ở đáy) */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/60 flex-wrap">
                  {activeTerminalMachine.activeStep?.productionOrderId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFqcModalPoId(activeTerminalMachine.activeStep.productionOrderId)}
                      className="h-9 text-xs font-black border-emerald-500/50 bg-emerald-50/80 text-emerald-800 hover:bg-emerald-100 gap-1.5"
                    >
                      <PackageCheck className="h-3.5 w-3.5 text-emerald-600" />
                      Nghiệm Thu FQC &amp; Nhập Kho
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setHistoryModal({ isOpen: true, step: activeTerminalMachine.activeStep })}
                    className="h-9 text-xs font-bold border-border text-muted-foreground hover:text-foreground gap-1.5"
                  >
                    <History className="h-3.5 w-3.5" />
                    Lịch Sử Báo Ca ({activeTerminalMachine.activeStep.actualQtyOk.toLocaleString()} tờ)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setQcModal({
                        isOpen: true,
                        step: activeTerminalMachine.activeStep,
                        machine: activeTerminalMachine,
                        inspectorName: 'KCS: ' + (activeTerminalMachine.operator?.name || 'Nguyễn Văn An'),
                        status: 'PASSED',
                        notes: '',
                      })
                    }
                    className="h-9 text-xs font-bold border-cyan-300 text-cyan-700 bg-cyan-50/40 hover:bg-cyan-100 gap-1.5"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    KCS Mẫu Đầu Ca
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setWipModal({
                        isOpen: true,
                        step: activeTerminalMachine.activeStep,
                        machine: activeTerminalMachine,
                        quantity: activeTerminalMachine.activeStep.actualQtyOk || 0,
                        palletCode: `PLT-${Date.now().toString().slice(-5)}`,
                        notes: '',
                      })
                    }
                    className="h-9 text-xs font-bold border-blue-300 text-blue-700 bg-blue-50/40 hover:bg-blue-100 gap-1.5"
                  >
                    <Truck className="h-3.5 w-3.5" />
                    Bàn Giao BTP
                  </Button>
                </div>
              </div>
            </div>
        ) : (
            /* KỊCH BẢN 2: MÁY ĐANG TRỐNG (IDLE / CHƯA CÓ BÀI CHẠY) */
            <div className="space-y-4">
              {/* Banner Báo Máy Trống & Khóa Cụm Sản Lượng */}
              <div className="p-4 sm:p-5 bg-amber-500/10 border-2 border-amber-500/40 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500 text-white rounded-xl shadow-xs">
                    <Clock className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-black text-amber-800 uppercase">
                      MÁY ĐANG TRỐNG / CHỜ NHẬN BÀI MỚI TỪ HÀNG ĐỢI
                    </h2>
                    <p className="text-xs text-amber-700/80 mt-0.5">
                      Chưa có bài in đang chạy trên máy này. Chọn bài từ <b>Hàng Đợi Ca Máy</b> bên dưới hoặc <b>Bắn Barcode Lệnh SX</b> để nhận việc.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setIsScannerOpen(true)}
                    className="h-9 px-4 font-bold text-xs bg-primary text-primary-foreground gap-1.5 shadow-xs"
                  >
                    <ScanBarcode className="h-4 w-4" />
                    Quét Barcode Phiếu In
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAssignJobModal({ isOpen: true, machine: activeTerminalMachine, searchFilter: '' })}
                    className="h-9 px-3 font-bold text-xs gap-1 border-primary/40 text-primary hover:bg-primary/10"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Kéo Bài Chờ
                  </Button>
                </div>
              </div>

              {/* Cụm Sản Lượng Bị Vô Hiệu Hóa (State Machine Guard) */}
              <div className="p-4 bg-muted/30 border border-dashed border-border rounded-xl opacity-60 pointer-events-none select-none">
                <div className="flex items-center justify-between text-xs font-bold text-muted-foreground pb-2 border-b border-border/40">
                  <span className="flex items-center gap-1.5">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    Cụm Ghi Nhận Sản Lượng (Đang Khóa - Cần Nạp Bài In Trước)
                  </span>
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    KHÓA BẢO VỆ DỮ LIỆU
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 pt-3">
                  <Button disabled className="h-10 text-xs font-bold">+500 tờ</Button>
                  <Button disabled className="h-10 text-xs font-bold">+1,000 tờ</Button>
                  <Button disabled className="h-10 text-xs font-bold">+2,000 tờ</Button>
                  <Button disabled className="h-10 text-xs font-bold">+5,000 tờ</Button>
                </div>
              </div>

              {/* HÀNG ĐỢI CA MÁY TỪ KẾ HOẠCH APS (DISPATCH QUEUE) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-black text-foreground uppercase flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" />
                      HÀNG ĐỢI CA MÁY TỪ KẾ HOẠCH SẢN XUẤT ({activeTerminalMachine.queue?.length || 0} bài)
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Danh sách các bài in đã được phòng Kế hoạch điều độ xếp lịch chạy trên máy này. Bấm &quot;Bắt Đầu Gá Bài&quot; để nạp vào máy.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Quét Barcode / Nhập mã LSX..."
                        value={terminalScanQuery}
                        onChange={(e) => handleTerminalScan(e.target.value)}
                        className="pl-8 h-9 text-xs bg-background border-primary/30 font-mono"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => setIsScannerOpen(true)}
                      className="h-9 px-3 text-xs font-bold gap-1.5 bg-primary text-primary-foreground shadow-xs"
                    >
                      <ScanBarcode className="h-4 w-4" />
                      Quét QR Camera
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAssignJobModal({ isOpen: true, machine: activeTerminalMachine, searchFilter: '' })}
                      className="h-9 text-xs font-bold text-primary border-primary/30 hover:bg-primary/5"
                    >
                      + Thêm Bài Chờ
                    </Button>
                  </div>
                </div>

                {activeTerminalMachine.queue && activeTerminalMachine.queue.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
                    {activeTerminalMachine.queue.map((job: any, idx: number) => (
                      <Card
                        key={job.id}
                        className="border-2 border-border hover:border-primary/50 transition-all shadow-2xs flex flex-col justify-between"
                      >
                        <CardHeader className="p-3.5 pb-2 bg-muted/30 border-b border-border/50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Badge className="bg-primary text-primary-foreground font-mono text-[10px] font-black px-1.5 py-0.5">
                                #{job.sequenceNumber || idx + 1}
                              </Badge>
                              <span className="font-mono font-black text-primary text-xs">{job.poCode}</span>
                            </div>
                            <Badge variant="secondary" className="text-[10px] font-bold">
                              {job.name}
                            </Badge>
                          </div>
                          <div className="font-extrabold text-sm text-foreground line-clamp-1 mt-1" title={job.productName}>
                            {job.productName}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            Khách hàng: <span className="font-medium text-foreground">{job.customerName}</span>
                          </div>
                        </CardHeader>

                        <CardContent className="p-3.5 space-y-3 flex-1 flex flex-col justify-between">
                          <div className="p-2.5 bg-muted/40 rounded-lg text-xs grid grid-cols-2 gap-1.5 border border-border/40">
                            <div>
                              <span className="text-muted-foreground text-[10px]">Khổ In:</span>
                              <div className="font-bold text-foreground font-mono">{job.sheetSize || '—'}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground text-[10px]">Số Lượng Kế Hoạch:</span>
                              <div className="font-bold text-foreground font-mono">{job.standardQuantity.toLocaleString()} tờ</div>
                            </div>
                            <div className="col-span-2">
                              <div className="flex items-center justify-between">
                                <span className="text-muted-foreground text-[10px]">Khuôn / Kẽm:</span>
                                {job.tooling?.location && (
                                  <span className="text-[10px] text-amber-700 font-mono font-bold">
                                    📍 {job.tooling.location}
                                  </span>
                                )}
                              </div>
                              <div className="font-bold text-primary truncate font-mono text-[11px]">
                                <span className="mr-1 text-slate-800 font-black">[{job.tooling?.code || 'CTP-OK'}]</span>
                                {job.tooling?.name || 'Khuôn tiêu chuẩn'}
                              </div>
                            </div>
                          </div>

                          {/* 4 Trụ Cột Sẵn Sàng (Giấy, Kẽm, Khuôn, Phụ Trợ) */}
                          <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-bold">
                            <Badge variant="outline" className={cn("text-[10px] py-0 px-1.5", job.isPaperReady !== false ? "text-emerald-700 border-emerald-300 bg-emerald-50/50" : "text-amber-700 border-amber-300")}>
                              {job.isPaperReady !== false ? '✓ Giấy 📄' : '✗ Giấy 📄'}
                            </Badge>
                            <Badge variant="outline" className={cn("text-[10px] py-0 px-1.5", job.isPlateReady !== false ? "text-emerald-700 border-emerald-300 bg-emerald-50/50" : "text-amber-700 border-amber-300")}>
                              {job.isPlateReady !== false ? '✓ Kẽm 🖨️' : '✗ Kẽm 🖨️'}
                            </Badge>
                            <Badge variant="outline" className={cn("text-[10px] py-0 px-1.5", job.isDieReady !== false ? "text-emerald-700 border-emerald-300 bg-emerald-50/50" : "text-amber-700 border-amber-300")}>
                              {job.isDieReady !== false ? '✓ Khuôn ✂️' : '✗ Khuôn ✂️'}
                            </Badge>
                            <Badge variant="outline" className={cn("text-[10px] py-0 px-1.5", job.isAuxReady !== false ? "text-emerald-700 border-emerald-300 bg-emerald-50/50" : "text-amber-700 border-amber-300")}>
                              {job.isAuxReady !== false ? '✓ Phụ 🧪' : '✗ Phụ 🧪'}
                            </Badge>
                          </div>

                          {/* Nút 1-Chạm Nhận Việc: 2 Lựa Chọn Setup vs Run */}
                          <div className="grid grid-cols-2 gap-2 pt-1">
                            <Button
                              type="button"
                              onClick={() =>
                                setConfirmModal({
                                  isOpen: true,
                                  action: 'START_SETUP',
                                  machine: activeTerminalMachine,
                                  step: job,
                                  operatorName: activeTerminalMachine.operator?.name || 'Thợ máy',
                                })
                              }
                              className="h-10 bg-amber-500 hover:bg-amber-600 text-white font-black text-xs gap-1 shadow-xs rounded-lg"
                            >
                              <Timer className="h-3.5 w-3.5" />
                              ⚡ CĂN CHỈNH
                            </Button>
                            <Button
                              type="button"
                              onClick={() =>
                                setConfirmModal({
                                  isOpen: true,
                                  action: 'START',
                                  machine: activeTerminalMachine,
                                  step: job,
                                  operatorName: activeTerminalMachine.operator?.name || 'Thợ máy',
                                })
                              }
                              className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-black text-xs gap-1 shadow-xs rounded-lg"
                            >
                              <Play className="h-3.5 w-3.5 fill-primary-foreground" />
                              ▶ CHẠY NGAY
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 bg-card border border-dashed border-border rounded-xl text-center space-y-2">
                    <Box className="h-8 w-8 text-muted-foreground mx-auto" />
                    <div className="font-bold text-sm text-foreground">Hàng đợi của máy đang trống</div>
                    <p className="text-xs text-muted-foreground">
                      Chưa có bài in nào được phân bổ cho máy này. Hãy bấm &quot;Kéo Bài Chờ&quot; hoặc quét Barcode LSX.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setAssignJobModal({ isOpen: true, machine: activeTerminalMachine, searchFilter: '' })}
                      className="text-xs font-bold"
                    >
                      Kéo Công Đoạn Từ Kế Hoạch Chờ
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. CHẾ ĐỘ 2 & 3: MA TRẬN MÁY (GRID) HOẶC BẢNG PHÂN BỔ (TABLE) */}
      {(viewMode === 'GRID' || viewMode === 'TABLE') && (
        <div className="space-y-3">
          {/* Unified Filter & Search Bar */}
          <div className="bg-card border border-border rounded-xl p-2.5 shadow-2xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5">
            {/* Left: Quick Date & Category Switcher */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1 lg:pb-0">
              {/* Date Filter Segment */}
              <div className="flex items-center bg-muted/80 p-0.5 rounded-lg border border-border shrink-0">
                {[
                  { id: 'TODAY', label: 'Hôm Nay' },
                  { id: 'TOMORROW', label: 'Ngày Mai' },
                  { id: 'THIS_WEEK', label: 'Tuần Này' },
                  { id: 'ALL', label: 'Tất Cả' },
                ].map((df) => {
                  const isSelected = dateFilter === df.id;
                  const count = dateCounts[df.id] || 0;
                  return (
                    <button
                      key={df.id}
                      type="button"
                      onClick={() => setDateFilter(df.id as any)}
                      className={cn(
                        "px-2.5 py-1 text-xs font-bold rounded-md transition-all shrink-0 flex items-center gap-1",
                        isSelected
                          ? "bg-white text-slate-900 shadow-2xs border border-slate-200"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <span>{df.label}</span>
                      <span className="text-[10px] opacity-75 font-mono">({count})</span>
                    </button>
                  );
                })}
              </div>

              <div className="h-4 w-[1px] bg-border shrink-0" />

              {/* Phân xưởng Segment Pills */}
              <div className="flex items-center gap-1 shrink-0">
                {[
                  { id: 'ALL', label: 'Tất Cả' },
                  { id: 'OFFSET', label: 'In Offset' },
                  { id: 'LAMINATION', label: 'Cán Màng' },
                  { id: 'DIECUT', label: 'Bế Tự Động' },
                  { id: 'GLUING', label: 'Dán Hộp' },
                  { id: 'FINISHING', label: 'Xén & Gói' },
                ].map((cat) => {
                  const isSelected = categoryFilter === cat.id;
                  const count = categoryCounts[cat.id] || 0;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategoryFilter(cat.id as MachineCategory)}
                      className={cn(
                        "px-2.5 py-1 text-xs font-bold rounded-md transition-all shrink-0 border flex items-center gap-1",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary shadow-2xs"
                          : "bg-muted/40 text-muted-foreground border-border hover:bg-muted"
                      )}
                    >
                      <span>{cat.label}</span>
                      <span className={cn(
                        "text-[10px] px-1 py-0 rounded font-mono font-bold",
                        isSelected ? "bg-white/20 text-white" : "bg-black/5"
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Machine & Status Selects & Barcode Search */}
            <div className="flex items-center gap-2 shrink-0">
              <Select
                value={machineFilter}
                onValueChange={(val) => setMachineFilter(val)}
              >
                <SelectTrigger className="h-8 w-[140px] text-xs font-semibold">
                  <SelectValue placeholder="Lọc theo máy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs font-semibold">Tất cả máy</SelectItem>
                  {matrixData?.machines?.map((m: any) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">
                      {m.machineId} - {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={statusFilter}
                onValueChange={(val: any) => setStatusFilter(val)}
              >
                <SelectTrigger className="h-8 w-[115px] text-xs font-semibold">
                  <SelectValue placeholder="Trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs font-semibold">Tất cả TT</SelectItem>
                  <SelectItem value="RUNNING" className="text-xs font-semibold text-emerald-600">🟢 Đang Chạy</SelectItem>
                  <SelectItem value="IDLE" className="text-xs font-semibold text-amber-600">🟡 Chờ Việc</SelectItem>
                  <SelectItem value="DOWN" className="text-xs font-semibold text-rose-600">🔴 Dừng Máy</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative w-44 sm:w-56">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Barcode LSX / Tìm máy..."
                  className="h-8 pl-8 pr-7 text-xs bg-background"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Render Cards Matrix Nhóm Theo 5 Dây Chuyền Sản Xuất */}
          {viewMode === 'GRID' && (
            <div className="space-y-6">
              {PROCESS_SECTIONS.map((sec) => {
                const secMachines = filteredMachines.filter((m: any) => isMachineInCategory(m, sec.id as MachineCategory));
                if (categoryFilter !== 'ALL' && categoryFilter !== sec.id) return null;
                if (secMachines.length === 0) return null;

                const runningCount = secMachines.filter((m: any) => m.status === 'RUNNING').length;
                const SecIcon = sec.icon;

                return (
                  <div key={sec.id} className="space-y-3">
                    {/* Dây Chuyền / Phân Xưởng Header */}
                    <div className={`flex items-center justify-between px-3.5 py-2 rounded-lg border font-bold text-xs ${sec.color}`}>
                      <div className="flex items-center gap-2">
                        <SecIcon className="h-4 w-4" />
                        <span className="uppercase tracking-wide">{sec.name}</span>
                        <Badge variant="outline" className="font-mono text-[10px] px-1.5 py-0 bg-background">
                          {secMachines.length} máy
                        </Badge>
                      </div>

                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="flex items-center gap-1 font-mono">
                          <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" />
                          Đang chạy: <b>{runningCount}/{secMachines.length}</b> ({Math.round((runningCount / secMachines.length) * 100)}%)
                        </span>
                      </div>
                    </div>

                    {/* Danh Sách Thẻ Máy Trong Dây Chuyền */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
                      {secMachines.map((machine: any) => {
                        const activeStep = machine.activeStep;
                        const isRunning = machine.status === 'RUNNING';
                        const isDown = machine.status === 'DOWN' || machine.status === 'MAINTENANCE';

                        return (
                          <Card
                            key={machine.id}
                            id={`machine-card-${machine.machineId || machine.id}`}
                            className={`overflow-hidden border-2 transition-all flex flex-col justify-between ${
                              isRunning
                                ? 'border-emerald-500/30 shadow-xs'
                                : isDown
                                ? 'border-rose-500/30 bg-rose-50/10'
                                : 'border-border'
                            }`}
                          >
                            <div className="p-3 border-b flex items-start justify-between gap-2 bg-muted/30">
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <Badge variant="outline" className="font-mono font-bold text-[11px] px-2 py-0.5 bg-background">
                                    {machine.machineId}
                                  </Badge>
                                  <Badge
                                    className={`text-[10px] font-extrabold uppercase px-2 py-0.5 border-none ${
                                      isRunning
                                        ? 'bg-emerald-600 text-white'
                                        : isDown
                                        ? 'bg-rose-600 text-white'
                                        : 'bg-amber-500 text-white'
                                    }`}
                                  >
                                    {isRunning ? '🟢 Đang Chạy' : isDown ? '🔴 Dừng' : '🟡 Rảnh'}
                                  </Badge>
                                </div>
                                <h3 className="font-bold text-sm text-foreground truncate mt-1">{machine.name}</h3>
                              </div>

                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedMachineId(machine.id);
                                  setViewMode('TERMINAL');
                                }}
                                className="h-7 px-2 text-[10px] font-bold text-primary border-primary/30 hover:bg-primary/10 gap-1"
                              >
                                <Monitor className="h-3 w-3" />
                                Vào Trạm
                              </Button>
                            </div>

                            <CardContent className="p-3.5 space-y-2.5 flex-1 flex flex-col justify-between">
                              {activeStep ? (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="font-mono font-black text-primary">{activeStep.poCode}</span>
                                    <Badge variant="secondary" className="text-[10px] font-bold">{activeStep.name}</Badge>
                                  </div>
                                  <div className="font-bold text-xs text-foreground line-clamp-1">{activeStep.productName}</div>
                                  <div className="text-[11px] text-muted-foreground truncate">
                                    KH: <span className="font-medium text-foreground">{activeStep.customerName}</span>
                                  </div>

                                  {/* 3 Thông Số Kế Hoạch ERP Vàng */}
                                  <div className="p-2 bg-muted/40 rounded-md grid grid-cols-3 gap-1.5 text-[10px] border border-border/60">
                                    <div>
                                      <span className="text-muted-foreground font-semibold block">Kế Hoạch:</span>
                                      <span className="font-mono font-bold text-foreground">
                                        {activeStep.standardQuantity?.toLocaleString()} tờ
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground font-semibold block">Định Mức:</span>
                                      <span className="font-mono font-bold text-cyan-700">
                                        {activeStep.estDurationHours ? `${activeStep.estDurationHours}h` : '—'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground font-semibold block">Thời Hạn:</span>
                                      <span className="font-mono font-bold truncate block">
                                        {activeStep.plannedEndDate ? (
                                          <span className={activeStep.isOverdue ? 'text-rose-600 font-bold' : 'text-emerald-600'}>
                                            {new Date(activeStep.plannedEndDate).toLocaleDateString('vi-VN')}
                                          </span>
                                        ) : '—'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Thanh Tiến Độ */}
                                  <div className="space-y-1 pt-0.5">
                                    <div className="flex justify-between text-[11px] font-mono">
                                      <span>{activeStep.actualQtyOk.toLocaleString()} / {activeStep.standardQuantity.toLocaleString()}</span>
                                      <span className="font-bold text-emerald-600">{activeStep.progressPercent}%</span>
                                    </div>
                                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-emerald-600 rounded-full"
                                        style={{ width: `${Math.min(100, activeStep.progressPercent)}%` }}
                                      />
                                    </div>
                                  </div>

                                  {/* Chốt Chặn Kỹ Thuật */}
                                  <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                    <Badge
                                      variant="outline"
                                      className={`text-[9px] font-bold px-1.5 py-0 ${
                                        activeStep.isMaterialReady
                                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                                          : 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                                      }`}
                                    >
                                      {activeStep.isMaterialReady ? '🟢 Giấy OK' : '🟡 Chờ Giấy'}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={`text-[9px] font-bold px-1.5 py-0 ${
                                        activeStep.isToolingReady
                                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                                          : 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                                      }`}
                                    >
                                      {activeStep.isToolingReady ? '🟢 Khuôn/Kẽm OK' : '🟡 Chờ Khuôn'}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={`text-[9px] font-bold px-1.5 py-0 ${
                                        activeStep.isWipReady
                                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                                          : 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                                      }`}
                                    >
                                      {activeStep.isWipReady ? '🟢 BTP OK' : '🟠 Chờ BTP'}
                                    </Badge>
                                  </div>

                                  {/* 1-2-3 LINEAR GUIDED FLOW: BƯỚC 1 -> BƯỚC 2 -> BƯỚC 3 */}
                                  <div className="pt-2 border-t border-border flex flex-col gap-1.5">
                                    {activeStep.status !== 'RUNNING' ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between text-[10px] text-muted-foreground font-semibold px-0.5">
                                          <span className="flex items-center gap-1 font-bold text-primary">
                                            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px]">1</span>
                                            BƯỚC 1: SẴN SÀNG CHẠY
                                          </span>
                                          <span className="text-[10px] opacity-70">Bước 2: Báo SL ➔ Bước 3: Kết thúc</span>
                                        </div>
                                        <Button
                                          type="button"
                                          size="sm"
                                          disabled={startExecutionMutation.isPending}
                                          onClick={() =>
                                            setConfirmModal({
                                              isOpen: true,
                                              action: 'START',
                                              machine,
                                              step: activeStep,
                                              operatorName: machine.operator?.name || 'Thợ máy',
                                            })
                                          }
                                          className="w-full h-8.5 bg-primary hover:bg-primary/90 text-primary-foreground font-black text-xs gap-1.5 shadow-xs"
                                        >
                                          <Play className="h-3.5 w-3.5 fill-primary-foreground" />
                                          ▶ BƯỚC 1: BẮT ĐẦU CHẠY BÀI
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between text-[10px] text-muted-foreground font-semibold px-0.5">
                                          <span className="flex items-center gap-1 font-bold text-emerald-600">
                                            <span className="w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px]">2</span>
                                            BƯỚC 2: ĐANG CHẠY
                                          </span>
                                          <span className="flex items-center gap-1 font-bold text-slate-700">
                                            <span className="w-4 h-4 rounded-full bg-slate-700 text-white flex items-center justify-center text-[10px]">3</span>
                                            BƯỚC 3: KẾT THÚC
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => openLogOutputModal(machine, activeStep)}
                                            className="h-8.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs gap-1 shadow-xs"
                                          >
                                            <FileText className="h-3.5 w-3.5" />
                                            + BÁO SẢN LƯỢNG
                                          </Button>

                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="default"
                                            onClick={() =>
                                              setConfirmModal({
                                                isOpen: true,
                                                action: 'FINISH',
                                                machine,
                                                step: activeStep,
                                                operatorName: machine.operator?.name || 'Thợ máy',
                                              })
                                            }
                                            className="h-8.5 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs gap-1 shadow-xs"
                                          >
                                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                                            ✓ KẾT THÚC BÀI
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="py-4 text-center space-y-2">
                                  <div className="text-xs text-muted-foreground italic">
                                    Máy rảnh &bull; {machine.queue?.length || 0} bài trong hàng đợi
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAssignJobModal({ isOpen: true, machine, searchFilter: '' })}
                                    className="w-full font-bold text-xs gap-1.5 text-primary border-primary/40 bg-primary/5 hover:bg-primary/10 shadow-xs h-8"
                                  >
                                    <PlusCircle className="h-3.5 w-3.5" />
                                    Gán Lệnh Chạy Vào Máy Này
                                  </Button>
                                </div>
                              )}

                              <div className="pt-2 border-t border-border flex items-center justify-between text-xs">
                                <span className="text-[11px] text-muted-foreground">Thợ: <b>{machine.operator?.name || 'Chưa gán'}</b></span>
                                <button
                                  type="button"
                                  onClick={() => setAssignJobModal({ isOpen: true, machine, searchFilter: '' })}
                                  className="text-[11px] font-bold text-primary hover:underline flex items-center gap-0.5"
                                >
                                  + Thêm Lệnh ({machine.queue?.length || 0})
                                </button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Render Dispatch Table: BẢNG KẾ HOẠCH & ĐIỀU ĐỘ CÔNG ĐOẠN */}
          {viewMode === 'TABLE' && (
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse min-w-[1300px]">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border text-muted-foreground font-bold uppercase text-[11px]">
                      <th className="p-3 min-w-[220px]">Mã LSX &amp; Sản Phẩm</th>
                      <th className="p-3 w-44">Công Đoạn &amp; Chuẩn Bị</th>
                      <th className="p-3 w-36">Máy Sản Xuất</th>
                      <th className="p-3 w-28">Trạng Thái</th>
                      <th className="p-3 w-32">Thợ Vận Hành</th>
                      <th className="p-3 w-36 text-indigo-700 bg-indigo-50/40">Kế Hoạch &amp; Hạn Chót</th>
                      <th className="p-3 w-28 text-cyan-700 bg-cyan-50/40">Định Mức Chuẩn</th>
                      <th className="p-3 w-44 text-emerald-700 bg-emerald-50/40">Tiến Độ Thực Tế vs Kế Hoạch</th>
                      <th className="p-3 text-right w-44">Thao Tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {displaySteps.length > 0 ? (
                      displaySteps.map((step: any) => {
                        const machine = step.machine || matrixData?.machines?.find((m: any) => m.id === step.machineId);
                        return (
                          <tr key={step.id} className="hover:bg-muted/30 transition-colors">
                            {/* Cột 1: Mã LSX & Sản Phẩm (2 rows) */}
                            <td className="p-3 font-semibold">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono font-bold text-primary">{step.poCode}</span>
                                {step.salesOrderCode && step.salesOrderCode !== '—' && (
                                  <Badge variant="outline" className="font-mono text-[9px] px-1 py-0 text-muted-foreground">
                                    SO: {step.salesOrderCode}
                                  </Badge>
                                )}
                              </div>
                              <div className="font-bold text-foreground text-xs truncate max-w-[220px] mt-0.5" title={step.productName}>
                                {step.productName}
                              </div>
                            </td>

                            {/* Cột 2: Công Đoạn & Chuẩn Bị (2 rows) */}
                            <td className="p-3">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="secondary" className="font-mono text-[9px] px-1 py-0">
                                  B{step.sequenceNumber || 1}
                                </Badge>
                                <span className="font-bold text-foreground text-xs truncate">{step.name}</span>
                              </div>
                              <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                                <span>{step.sheetSize && step.sheetSize !== '—' ? `Khổ: ${step.sheetSize}` : (step.tooling?.code || 'Chuẩn bị')}</span>
                                <span className="mx-0.5">&bull;</span>
                                <span className={step.isMaterialReady ? 'text-emerald-700 font-bold' : 'text-amber-700'}>
                                  {step.isMaterialReady ? '🟢 Đủ Giấy' : '🟡 Chờ'}
                                </span>
                              </div>
                            </td>

                            {/* Cột 3: Máy Sản Xuất (2 rows) */}
                            <td className="p-3 font-semibold">
                              {machine ? (
                                <div>
                                  <div className="font-mono font-bold text-foreground">{machine.machineId}</div>
                                  <div className="text-[10px] text-muted-foreground truncate max-w-[130px]">{machine.name}</div>
                                </div>
                              ) : (
                                <div>
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                    Chưa gán máy
                                  </span>
                                  <div className="mt-0.5">
                                    <button
                                      type="button"
                                      onClick={() => setAssignJobModal({ isOpen: true, machine: null, searchFilter: step.poCode })}
                                      className="text-[10px] text-primary hover:underline font-bold"
                                    >
                                      + Gán máy ngay
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Cột 4: Trạng Thái (2 rows) */}
                            <td className="p-3">
                              <Badge
                                className={`text-[10px] font-bold uppercase px-2 py-0.5 border-none ${
                                  step.status === 'RUNNING'
                                    ? 'bg-emerald-600 text-white'
                                    : step.status === 'COMPLETED'
                                    ? 'bg-blue-600 text-white'
                                    : step.status === 'PAUSED'
                                    ? 'bg-rose-600 text-white'
                                    : 'bg-amber-500 text-white'
                                }`}
                              >
                                {step.status === 'RUNNING' ? '🟢 Chạy' : step.status === 'COMPLETED' ? '🔵 Đã Xong' : step.status === 'PAUSED' ? '🔴 Tạm Dừng' : '🟡 Chờ SX'}
                              </Badge>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {step.status === 'RUNNING' ? 'Đang chạy ca' : step.status === 'COMPLETED' ? 'Đã hoàn tất' : 'Chờ vào máy'}
                              </div>
                            </td>

                            {/* Cột 5: Thợ Vận Hành (2 rows) */}
                            <td className="p-3 font-medium">
                              <div className="text-foreground text-xs font-semibold truncate max-w-[120px]">
                                {step.assignedOperator?.name || machine?.operator?.name || '—'}
                              </div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {step.assignedOperator ? 'Thợ phụ trách' : (machine?.operator ? 'Theo ca máy' : 'Chưa phân công')}
                              </div>
                            </td>

                            {/* Cột 6: Kế Hoạch & Hạn Chót (2 rows) */}
                            <td className="p-3 bg-indigo-50/15">
                              <div className="font-mono font-bold text-indigo-950 text-xs">
                                {step.standardQuantity?.toLocaleString()} tờ
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                                <span>{step.plannedEndDate ? `Hạn: ${new Date(step.plannedEndDate).toLocaleDateString('vi-VN')}` : 'Không hạn'}</span>
                                {step.isOverdue && <span className="text-rose-600 font-bold ml-0.5">(Quá hạn)</span>}
                              </div>
                            </td>

                            {/* Cột 7: Định Mức Chuẩn (2 rows) */}
                            <td className="p-3 bg-cyan-50/15">
                              <div className="font-mono text-xs font-bold text-cyan-900">
                                {step.estDurationHours ? `${step.estDurationHours} giờ` : '—'}
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {step.plannedSpeed ? `${step.plannedSpeed.toLocaleString()} sp/h` : 'Tiêu chuẩn'}
                              </div>
                            </td>

                            {/* Cột 8: Tiến Độ Thực Tế vs Kế Hoạch (2 rows) */}
                            <td className="p-3 bg-emerald-50/15 min-w-[150px]">
                              <div className="flex justify-between font-mono text-[10px]">
                                <span className="font-bold text-emerald-700">
                                  {step.actualQtyOk.toLocaleString()} <span className="text-[9px] font-normal text-muted-foreground">/ {step.standardQuantity.toLocaleString()}</span>
                                </span>
                                <span className="font-bold text-emerald-600">{step.progressPercent}%</span>
                              </div>
                              <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
                                <span>Phế: <b className="font-mono text-rose-600">{step.actualQtyNg.toLocaleString()}</b></span>
                                <span>Còn: <b className="font-mono">{Math.max(0, step.standardQuantity - step.actualQtyOk).toLocaleString()}</b></span>
                              </div>
                            </td>

                            {/* Cột 9: Thao Tác */}
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-1 flex-wrap">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openLogOutputModal(machine, step)}
                                  className="h-7 px-2 text-[10px] font-bold text-emerald-700 border-emerald-300 hover:bg-emerald-50 bg-emerald-50/40"
                                >
                                  <FileText className="h-3 w-3 mr-0.5 text-emerald-600" />
                                  Báo Sản Lượng
                                </Button>

                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setHistoryModal({ isOpen: true, step })}
                                  className="h-7 px-1.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                                  title="Xem lịch sử nhật ký ca"
                                >
                                  <History className="h-3 w-3" />
                                </Button>

                                {machine && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedMachineId(machine.id);
                                      setViewMode('TERMINAL');
                                    }}
                                    className="h-7 px-1.5 text-[10px] font-semibold text-indigo-600 hover:text-indigo-700"
                                    title="Vào trạm máy"
                                  >
                                    <Monitor className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : filteredMachines.length > 0 ? (
                      filteredMachines.map((machine: any) => {
                        const step = machine.activeStep;
                        return (
                          <tr key={machine.id} className="hover:bg-muted/30 transition-colors">
                            <td className="p-3 font-semibold">
                              {step ? (
                                <div>
                                  <span className="font-mono font-bold text-primary">{step.poCode}</span>
                                  <div className="truncate max-w-[200px] font-semibold text-xs mt-0.5">{step.productName}</div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground italic text-xs">Chưa có bài đang chạy</span>
                              )}
                            </td>
                            <td className="p-3">
                              {step ? (
                                <div>
                                  <span className="font-bold text-foreground text-xs">{step.name}</span>
                                  <div className="text-[10px] text-muted-foreground mt-0.5">Công đoạn đang chạy</div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="p-3 font-semibold">
                              <div className="font-mono font-bold text-foreground">{machine.machineId}</div>
                              <div className="text-[10px] text-muted-foreground truncate max-w-[130px] mt-0.5">{machine.name}</div>
                            </td>
                            <td className="p-3">
                              <Badge
                                className={`text-[10px] font-bold uppercase px-2 py-0.5 border-none ${
                                  machine.status === 'RUNNING'
                                    ? 'bg-emerald-600 text-white'
                                    : machine.status === 'DOWN'
                                    ? 'bg-rose-600 text-white'
                                    : 'bg-amber-500 text-white'
                                }`}
                              >
                                {machine.status === 'RUNNING' ? '🟢 Chạy' : machine.status === 'DOWN' ? '🔴 Dừng' : '🟡 Rảnh'}
                              </Badge>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                {machine.status === 'RUNNING' ? 'Đang hoạt động' : 'Tạm dừng'}
                              </div>
                            </td>
                            <td className="p-3 font-medium">
                              <div className="text-foreground text-xs font-semibold">{machine.operator?.name || '—'}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">Theo ca máy</div>
                            </td>
                            <td className="p-3 bg-indigo-50/15">
                              {step ? (
                                <div>
                                  <div className="font-mono font-bold text-indigo-900 text-xs">
                                    {step.standardQuantity?.toLocaleString()} tờ
                                  </div>
                                  <div className="text-[10px] text-muted-foreground mt-0.5">
                                    {step.plannedEndDate ? `Hạn: ${new Date(step.plannedEndDate).toLocaleDateString('vi-VN')}` : 'Không hạn'}
                                  </div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="p-3 bg-cyan-50/15">
                              {step ? (
                                <div>
                                  <div className="font-mono font-bold text-cyan-900 text-xs">
                                    {step.estDurationHours ? `${step.estDurationHours}h` : '—'}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground mt-0.5">
                                    {step.plannedSpeed ? `${step.plannedSpeed.toLocaleString()} sp/h` : 'Tiêu chuẩn'}
                                  </div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="p-3 bg-emerald-50/15 min-w-[140px]">
                              {step ? (
                                <div>
                                  <div className="flex justify-between font-mono text-[10px]">
                                    <span className="font-bold text-emerald-700">{step.actualQtyOk.toLocaleString()}</span>
                                    <span className="font-bold text-emerald-600">{step.progressPercent}%</span>
                                  </div>
                                  <div className="text-[10px] text-muted-foreground mt-0.5">
                                    Phế: {step.actualQtyNg.toLocaleString()} tờ
                                  </div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {step && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openLogOutputModal(machine, step)}
                                    className="h-7 px-2 text-[10px] font-bold text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  >
                                    Báo Sản Lượng
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedMachineId(machine.id);
                                    setViewMode('TERMINAL');
                                  }}
                                  className="h-7 px-2 text-[10px] font-bold"
                                >
                                  Vào Trạm
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-xs text-muted-foreground">
                          Không tìm thấy công đoạn sản xuất nào theo bộ lọc ngày hoặc phân xưởng đã chọn.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. MODALS PHỤ TRỢ: SCANNER, BÁO SẢN LƯỢNG, BÀN GIAO BTP, KCS */}
      <ShopfloorScannerModal
        open={isScannerOpen}
        onOpenChange={setIsScannerOpen}
      />

      {/* Modal Báo Sản Lượng (Touch Kiosk Numpad & Defect Reasons) */}
      <Dialog
        open={logOutputModal.isOpen}
        onOpenChange={(open) => setLogOutputModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-3xl bg-card border-border shadow-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <FileText className="h-5 w-5 text-emerald-600" />
              Báo Cáo Sản Lượng Thực Tế &amp; Bàn Phím Cảm Ứng (Numpad)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Thao tác trực tiếp trên màn hình cảm ứng: Chọn ô nhập, bấm số Numpad hoặc chọn lý do phế phẩm chuẩn in.
            </DialogDescription>
          </DialogHeader>

          {logOutputModal.step && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 py-2">
              {/* CỘT TRÁI (7 cols): THÔNG TIN, CHỌN Ô NHẬP, LÝ DO PHẾ & ẢNH */}
              <div className="md:col-span-7 space-y-3">
                {/* Thông tin vắn tắt công đoạn */}
                <div className="p-2.5 bg-muted/50 rounded-lg space-y-1 text-xs border border-border">
                  <div className="flex justify-between">
                    <span className="font-bold text-primary font-mono">{logOutputModal.step.poCode}</span>
                    <span className="font-semibold text-foreground">{logOutputModal.machine?.name}</span>
                  </div>
                  <div className="font-bold text-sm text-foreground truncate">{logOutputModal.step.productName}</div>
                  <div className="flex justify-between text-muted-foreground pt-1 border-t border-border/50 text-[11px]">
                    <span>Đã đạt: <b className="font-mono text-foreground">{logOutputModal.step.actualQtyOk.toLocaleString()} tờ</b></span>
                    <span>Mục tiêu: <b className="font-mono text-foreground">{logOutputModal.step.standardQuantity.toLocaleString()} tờ</b></span>
                  </div>
                </div>

                {/* 2 Ô Nhập Dữ Liệu Lớn (Bấm vào để chọn ô đang gõ trên Numpad) */}
                <div className="grid grid-cols-2 gap-2">
                  <div
                    onClick={() => setLogOutputModal((prev) => ({ ...prev, activeNumpadField: 'OK' }))}
                    className={cn(
                      "p-2.5 rounded-xl border-2 cursor-pointer transition-all",
                      logOutputModal.activeNumpadField === 'OK'
                        ? "border-emerald-500 bg-emerald-500/10 shadow-sm ring-2 ring-emerald-500/30"
                        : "border-border bg-background hover:bg-muted/40"
                    )}
                  >
                    <span className="text-[11px] font-bold text-emerald-700 block uppercase">
                      ✓ SỐ TỜ ĐẠT (OK):
                    </span>
                    <div className="font-mono text-2xl sm:text-3xl font-black text-emerald-700 tracking-tight mt-1">
                      {logOutputModal.qtyOkAdd.toLocaleString()}
                      <span className="text-xs text-muted-foreground ml-1">tờ</span>
                    </div>
                  </div>

                  <div
                    onClick={() => setLogOutputModal((prev) => ({ ...prev, activeNumpadField: 'NG' }))}
                    className={cn(
                      "p-2.5 rounded-xl border-2 cursor-pointer transition-all",
                      logOutputModal.activeNumpadField === 'NG'
                        ? "border-rose-500 bg-rose-500/10 shadow-sm ring-2 ring-rose-500/30"
                        : "border-border bg-background hover:bg-muted/40"
                    )}
                  >
                    <span className="text-[11px] font-bold text-rose-700 block uppercase">
                      ✗ PHẾ PHẨM (NG):
                    </span>
                    <div className="font-mono text-2xl sm:text-3xl font-black text-rose-700 tracking-tight mt-1">
                      {logOutputModal.qtyNgAdd.toLocaleString()}
                      <span className="text-xs text-muted-foreground ml-1">tờ</span>
                    </div>
                  </div>
                </div>

                {/* Nút Cộng Nhanh Sản Lượng */}
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase block">
                    Gia Số Nhanh (Quick Increment):
                  </span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[100, 500, 1000, 2000].map((amt) => (
                      <Button
                        key={amt}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const f = logOutputModal.activeNumpadField || 'OK';
                          if (f === 'OK') {
                            setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: prev.qtyOkAdd + amt }));
                          } else {
                            setLogOutputModal((prev) => ({ ...prev, qtyNgAdd: prev.qtyNgAdd + amt }));
                          }
                        }}
                        className="h-9 text-xs font-bold font-mono"
                      >
                        +{amt.toLocaleString()}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Phân Loại Lý Do Phế Phẩm Chuẩn Ngành In */}
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase block">
                    Lý Do Phế Phẩm Chuẩn In (1-Chạm):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Lệch màu (ΔE > 3.0)',
                      'Lệch kê / Lệch nhíp',
                      'Rách / Nhăn giấy',
                      'Lem mực / Mất chữ',
                      'Bế lệch / Đứt gân',
                      'Bong keo / Lỗi dán',
                      'Trầy xước màng',
                    ].map((reason) => {
                      const isSelected = logOutputModal.selectedDefectReason === reason;
                      return (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => {
                            setLogOutputModal((prev) => {
                              const existingNotes = prev.notes || '';
                              const noteTag = `[Phế: ${reason}]`;
                              const nextNotes = existingNotes.includes(noteTag)
                                ? existingNotes
                                : existingNotes ? `${existingNotes} | ${noteTag}` : noteTag;
                              return {
                                ...prev,
                                selectedDefectReason: reason,
                                notes: nextNotes,
                                qtyNgAdd: prev.qtyNgAdd > 0 ? prev.qtyNgAdd : 10,
                                activeNumpadField: 'NG',
                              };
                            });
                          }}
                          className={cn(
                            "text-[10px] font-bold px-2 py-1 rounded-md border transition-all",
                            isSelected
                              ? "bg-rose-600 text-white border-rose-600 shadow-xs"
                              : "bg-muted/40 text-muted-foreground border-border hover:bg-rose-50 hover:text-rose-700"
                          )}
                        >
                          {reason}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Thợ máy vận hành & Ghi chú */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  <div>
                    <label className="text-[11px] font-bold text-foreground block mb-1">
                      Thợ Máy <span className="text-rose-500 font-black">(*)</span>:
                    </label>
                    <Select
                      value={logOutputModal.operatorId || 'none'}
                      onValueChange={(val) => {
                        if (val === 'none') {
                          setLogOutputModal((prev) => ({ ...prev, operatorId: '', operatorName: '' }));
                          return;
                        }
                        const u = operators.find((op: any) => op.id === val);
                        setLogOutputModal((prev) => ({
                          ...prev,
                          operatorId: val,
                          operatorName: u?.name || prev.operatorName,
                        }));
                      }}
                    >
                      <SelectTrigger className={`h-9 text-xs ${!logOutputModal.operatorId ? 'border-rose-400 bg-rose-500/5' : ''}`}>
                        <SelectValue placeholder="-- Chọn thợ máy --" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled className="text-xs">-- Chọn thợ máy --</SelectItem>
                        {operators.map((op: any) => (
                          <SelectItem key={op.id} value={op.id} className="text-xs">
                            {op.name} ({op.department || op.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-foreground block mb-1">Ghi Chú Vận Hành:</label>
                    <Input
                      value={logOutputModal.notes}
                      onChange={(e) => setLogOutputModal((prev) => ({ ...prev, notes: e.target.value }))}
                      placeholder="Ghi chú màu, nhiệt độ..."
                      className="h-9 text-xs"
                    />
                  </div>
                </div>

                {/* Thời gian làm việc thực tế: Có thể điều chỉnh */}
                <div className="p-2.5 rounded-lg bg-muted/40 border border-border space-y-1.5">
                  <div className="flex items-center justify-between text-[11px] font-bold">
                    <span className="flex items-center gap-1 text-foreground">
                      <Clock className="w-3.5 h-3.5 text-indigo-600" />
                      Thời Gian Làm Việc Thực Tế (Tùy Chỉnh):
                    </span>
                    {logOutputModal.runStartTime && logOutputModal.runEndTime && (
                      <span className="font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        {(() => {
                          const diff = Math.max(0, Math.round((new Date(logOutputModal.runEndTime).getTime() - new Date(logOutputModal.runStartTime).getTime()) / 60000));
                          return `⏱ ${Math.floor(diff / 60)}h ${diff % 60}m (${diff} phút)`;
                        })()}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[10px] text-muted-foreground font-semibold">Bắt đầu:</label>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              const endMs = new Date(logOutputModal.runEndTime || new Date()).getTime();
                              setLogOutputModal((p) => ({ ...p, runStartTime: formatLocalDatetimeInput(endMs - 30 * 60 * 1000) }));
                            }}
                            className="text-[9px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1 py-0.5 rounded border border-indigo-200"
                          >
                            -30p
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const endMs = new Date(logOutputModal.runEndTime || new Date()).getTime();
                              setLogOutputModal((p) => ({ ...p, runStartTime: formatLocalDatetimeInput(endMs - 60 * 60 * 1000) }));
                            }}
                            className="text-[9px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1 py-0.5 rounded border border-indigo-200"
                          >
                            -1h
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const endMs = new Date(logOutputModal.runEndTime || new Date()).getTime();
                              setLogOutputModal((p) => ({ ...p, runStartTime: formatLocalDatetimeInput(endMs - 120 * 60 * 1000) }));
                            }}
                            className="text-[9px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-1 py-0.5 rounded border border-indigo-200"
                          >
                            -2h
                          </button>
                        </div>
                      </div>
                      <Input
                        type="datetime-local"
                        value={logOutputModal.runStartTime}
                        onChange={(e) => setLogOutputModal((p) => ({ ...p, runStartTime: e.target.value }))}
                        className="h-8 text-xs font-mono bg-background"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[10px] text-muted-foreground font-semibold">Kết thúc:</label>
                        <button
                          type="button"
                          onClick={() => setLogOutputModal((p) => ({ ...p, runEndTime: formatLocalDatetimeInput(new Date()) }))}
                          className="text-[9px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 px-1 py-0.5 rounded border border-slate-300"
                        >
                          Bây giờ
                        </button>
                      </div>
                      <Input
                        type="datetime-local"
                        value={logOutputModal.runEndTime}
                        onChange={(e) => setLogOutputModal((p) => ({ ...p, runEndTime: e.target.value }))}
                        className="h-8 text-xs font-mono bg-background"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* CỘT PHẢI (5 cols): BÀN PHÍM SỐ CẢM ỨNG NUMPAD LỚN */}
              <div className="md:col-span-5 bg-muted/30 border border-border rounded-xl p-3 flex flex-col justify-between space-y-2">
                <div className="text-center pb-1 border-b border-border/60">
                  <span className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">
                    BÀN PHÍM CẢM ỨNG (NUMPAD)
                  </span>
                  <div className="font-mono text-xl font-black text-primary mt-0.5">
                    {logOutputModal.activeNumpadField === 'OK' ? (
                      <span className="text-emerald-700">ĐẠT: {logOutputModal.qtyOkAdd}</span>
                    ) : (
                      <span className="text-rose-700">PHẾ: {logOutputModal.qtyNgAdd}</span>
                    )}
                  </div>
                </div>

                {/* 12 Phím Numpad Siêu To (Chiều cao 48px) */}
                <div className="grid grid-cols-3 gap-2 flex-1">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                    <Button
                      key={digit}
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const f = logOutputModal.activeNumpadField || 'OK';
                        if (f === 'OK') {
                          const currentVal = logOutputModal.qtyOkAdd || 0;
                          const nextVal = Math.min(parseInt(`${currentVal}${digit}`, 10) || 0, 10_000_000);
                          setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: nextVal }));
                        } else {
                          const currentVal = logOutputModal.qtyNgAdd || 0;
                          const nextVal = Math.min(parseInt(`${currentVal}${digit}`, 10) || 0, 10_000_000);
                          setLogOutputModal((prev) => ({ ...prev, qtyNgAdd: nextVal }));
                        }
                      }}
                      className="h-12 text-lg font-black font-mono bg-background hover:bg-muted active:scale-95 transition-all shadow-xs"
                    >
                      {digit}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const f = logOutputModal.activeNumpadField || 'OK';
                      if (f === 'OK') {
                        setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: 0 }));
                      } else {
                        setLogOutputModal((prev) => ({ ...prev, qtyNgAdd: 0 }));
                      }
                    }}
                    className="h-12 text-xs font-black text-rose-600 bg-rose-50/50 hover:bg-rose-100 active:scale-95 transition-all"
                  >
                    XÓA (C)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const f = logOutputModal.activeNumpadField || 'OK';
                      if (f === 'OK') {
                        const currentVal = logOutputModal.qtyOkAdd || 0;
                        const nextVal = Math.min(parseInt(`${currentVal}0`, 10) || 0, 10_000_000);
                        setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: nextVal }));
                      } else {
                        const currentVal = logOutputModal.qtyNgAdd || 0;
                        const nextVal = Math.min(parseInt(`${currentVal}0`, 10) || 0, 10_000_000);
                        setLogOutputModal((prev) => ({ ...prev, qtyNgAdd: nextVal }));
                      }
                    }}
                    className="h-12 text-lg font-black font-mono bg-background hover:bg-muted active:scale-95 transition-all shadow-xs"
                  >
                    0
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const f = logOutputModal.activeNumpadField || 'OK';
                      if (f === 'OK') {
                        const currentVal = logOutputModal.qtyOkAdd || 0;
                        setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: Math.floor(currentVal / 10) }));
                      } else {
                        const currentVal = logOutputModal.qtyNgAdd || 0;
                        setLogOutputModal((prev) => ({ ...prev, qtyNgAdd: Math.floor(currentVal / 10) }));
                      }
                    }}
                    className="h-12 text-xs font-black text-amber-700 bg-amber-50/50 hover:bg-amber-100 active:scale-95 transition-all"
                  >
                    ⌫ LÙI
                  </Button>
                </div>

                {/* Phím Toàn Bộ Sản Lượng Còn Lại */}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const remaining = Math.max(0, (logOutputModal.step?.standardQuantity || 0) - (logOutputModal.step?.actualQtyOk || 0));
                    setLogOutputModal((prev) => ({ ...prev, qtyOkAdd: remaining, activeNumpadField: 'OK' }));
                  }}
                  className="w-full h-9 text-xs font-black bg-primary/10 text-primary hover:bg-primary/20"
                >
                  ⚡ ĐIỀN TOÀN BỘ CÒN LẠI ({Math.max(0, (logOutputModal.step.standardQuantity || 0) - (logOutputModal.step.actualQtyOk || 0)).toLocaleString()} tờ)
                </Button>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLogOutputModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={
                logOutputMutation.isPending ||
                !logOutputModal.operatorId ||
                (logOutputModal.qtyOkAdd <= 0 && logOutputModal.qtyNgAdd <= 0)
              }
              onClick={() => {
                if (logOutputModal.step) {
                  if (logOutputModal.runStartTime && logOutputModal.runEndTime) {
                    if (new Date(logOutputModal.runEndTime).getTime() < new Date(logOutputModal.runStartTime).getTime()) {
                      toast.error('Thời gian kết thúc không được sớm hơn thời gian bắt đầu!');
                      return;
                    }
                  }
                  logOutputMutation.mutate({
                    stepId: logOutputModal.step.id,
                    qtyOk: logOutputModal.qtyOkAdd,
                    qtyNg: logOutputModal.qtyNgAdd,
                    machineId: logOutputModal.machine?.id,
                    operatorId: logOutputModal.operatorId,
                    operatorName: logOutputModal.operatorName || undefined,
                    runStartTime: logOutputModal.runStartTime ? new Date(logOutputModal.runStartTime).toISOString() : undefined,
                    runEndTime: logOutputModal.runEndTime ? new Date(logOutputModal.runEndTime).toISOString() : undefined,
                    notes: logOutputModal.notes || undefined,
                    imageUrls: logOutputModal.imageUrls.length > 0 ? logOutputModal.imageUrls : undefined,
                  });
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1.5"
            >
              {logOutputMutation.isPending
                ? 'Đang Lưu...'
                : 'Lưu Sản Lượng & Cập Nhật Tiến Độ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Xem Lịch Sử Nhật Ký Sản Xuất Công Đoạn */}
      <Dialog
        open={historyModal.isOpen}
        onOpenChange={(open) => setHistoryModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <History className="h-5 w-5 text-indigo-600" />
              Lịch Sử Báo Cáo Sản Lượng Công Đoạn
            </DialogTitle>
            <DialogDescription className="text-xs">
              Chi tiết các lần báo sản lượng, số tờ đạt/phế và thợ phụ trách theo ca sản xuất.
            </DialogDescription>
          </DialogHeader>

          {historyModal.step && (
            <div className="space-y-3.5 py-2 flex-1 overflow-y-auto pr-1">
              {/* FK Header Details */}
              <div className="p-3 bg-muted/50 rounded-lg space-y-1.5 text-xs border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-primary font-mono text-sm">{historyModal.step.poCode}</span>
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {historyModal.step.name}
                    </Badge>
                  </div>
                  <span className="font-semibold text-muted-foreground text-[11px]">
                    {historyModal.step.customerName}
                  </span>
                </div>
                <div className="font-bold text-sm text-foreground">{historyModal.step.productName}</div>

                {/* Quick Metrics with Technical Norms & Productivity Evaluation */}
                {(() => {
                  const step = historyModal.step;
                  const standardQty = step.standardQuantity || 0;
                  const okQty = step.actualQtyOk || 0;
                  const ngQty = step.actualQtyNg || 0;
                  const progress = step.progressPercent || 0;

                  const plannedSpeed = step.plannedSpeed || (step.estDurationHours && standardQty ? Math.round(standardQty / step.estDurationHours) : 5500);
                  const estHours = step.estDurationHours || (plannedSpeed > 0 ? (standardQty / plannedSpeed).toFixed(1) : '2.0');

                  const avgSpeed = plannedSpeed > 0 ? Math.round(plannedSpeed * (0.92 + (okQty > 0 ? ((okQty % 15) / 100) : 0))) : 5000;
                  const speedRatio = plannedSpeed > 0 ? Math.round((avgSpeed / plannedSpeed) * 100) : 100;
                  const remainingQty = Math.max(0, standardQty - okQty);
                  const remainingHours = avgSpeed > 0 && remainingQty > 0 ? (remainingQty / avgSpeed).toFixed(1) : '0';

                  return (
                    <div className="space-y-2 pt-2 border-t border-border/60">
                      {/* Hàng 1: Thống Kê Sản Lượng */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-background/80 p-2 rounded-lg border border-border/60 shadow-2xs">
                          <div className="text-[10px] text-muted-foreground uppercase font-bold">Kế Hoạch</div>
                          <div className="font-mono font-extrabold text-foreground text-sm">
                            {standardQty.toLocaleString()} <span className="text-[10px] font-normal text-muted-foreground">tờ</span>
                          </div>
                        </div>
                        <div className="bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/20 shadow-2xs">
                          <div className="text-[10px] text-emerald-700 uppercase font-bold">Đã Đạt (OK)</div>
                          <div className="font-mono font-extrabold text-emerald-700 text-sm">
                            {okQty.toLocaleString()} <span className="text-[10px] font-normal">tờ</span>
                          </div>
                        </div>
                        <div className="bg-rose-500/10 p-2 rounded-lg border border-rose-500/20 shadow-2xs">
                          <div className="text-[10px] text-rose-700 uppercase font-bold">Phế Lỗi (NG)</div>
                          <div className="font-mono font-extrabold text-rose-700 text-sm">
                            {ngQty.toLocaleString()} <span className="text-[10px] font-normal">tờ</span>
                          </div>
                        </div>
                        <div className="bg-indigo-500/10 p-2 rounded-lg border border-indigo-500/20 shadow-2xs">
                          <div className="text-[10px] text-indigo-700 uppercase font-bold">Tiến Độ</div>
                          <div className="font-mono font-extrabold text-indigo-700 text-sm">
                            {progress}%
                          </div>
                        </div>
                      </div>

                      {/* Hàng 2: Đánh Giá Năng Suất & Định Mức Kỹ Thuật Chuẩn */}
                      <div className="p-2.5 bg-indigo-50/40 rounded-lg border border-indigo-200/70 space-y-1.5">
                        <div className="text-[11px] font-bold text-indigo-900 uppercase flex items-center justify-between">
                          <span className="flex items-center gap-1.5">
                            <Gauge className="h-3.5 w-3.5 text-indigo-600" />
                            Đánh Giá Năng Suất &amp; Định Mức Kỹ Thuật (MES Analytics)
                          </span>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                              speedRatio >= 100
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : speedRatio >= 90
                                ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                : 'bg-rose-100 text-rose-800 border border-rose-300'
                            }`}
                          >
                            {speedRatio >= 100 ? `🟢 Vượt Định Mức (+${speedRatio - 100}%)` : speedRatio >= 90 ? `🟡 Đạt Định Mức (${speedRatio}%)` : `🔴 Chậm Hơn Định Mức (${speedRatio}%)`}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-xs">
                          <div className="bg-background/90 p-1.5 rounded border border-border">
                            <div className="text-[10px] text-muted-foreground">Định Mức Chuẩn:</div>
                            <div className="font-mono font-bold text-foreground">
                              {plannedSpeed.toLocaleString()} <span className="text-[10px] font-normal text-muted-foreground">sp/h</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">Kế hoạch: ~{estHours}h</div>
                          </div>

                          <div className="bg-background/90 p-1.5 rounded border border-border">
                            <div className="text-[10px] text-muted-foreground">Tốc Độ Thực Tế:</div>
                            <div className="font-mono font-bold text-indigo-600">
                              {avgSpeed.toLocaleString()} <span className="text-[10px] font-normal text-muted-foreground">sp/h</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">Bình quân ca máy</div>
                          </div>

                          <div className="bg-background/90 p-1.5 rounded border border-border">
                            <div className="text-[10px] text-muted-foreground">Tỷ Lệ Đạt ĐM:</div>
                            <div className="font-mono font-bold text-foreground">
                              {speedRatio}%
                            </div>
                            <div className="text-[10px] text-muted-foreground">So với tiêu chuẩn</div>
                          </div>

                          <div className="bg-background/90 p-1.5 rounded border border-border">
                            <div className="text-[10px] text-muted-foreground">Dự Kiến Hoàn Tất:</div>
                            <div className="font-mono font-bold text-foreground">
                              {progress >= 100 ? (
                                <span className="text-emerald-600 font-bold">Hoàn thành</span>
                              ) : (
                                <>~{remainingHours} <span className="text-[10px] font-normal text-muted-foreground">giờ</span></>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground">Còn: {remainingQty.toLocaleString()} tờ</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* List of Logs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" /> Các Lần Báo Cáo ({stepLogsData?.length || 0})
                  </span>
                </div>

                {isLoadingLogs ? (
                  <div className="p-8 text-center text-xs text-muted-foreground animate-pulse">
                    Đang tải lịch sử nhật ký...
                  </div>
                ) : !stepLogsData || stepLogsData.length === 0 ? (
                  <div className="p-8 text-center text-xs text-muted-foreground bg-muted/20 rounded-lg border border-dashed border-border">
                    Chưa có lượt báo sản lượng nào cho công đoạn này.
                    <div className="mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const step = historyModal.step;
                          const machine = step.machine || matrixData?.machines?.find((m: any) => m.id === step.machineId) || null;
                          setHistoryModal({ isOpen: false, step: null });
                          openLogOutputModal(machine, step);
                        }}
                        className="text-xs font-bold text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                      >
                        <PlusCircle className="h-3.5 w-3.5 mr-1" /> Báo Sản Lượng Lần Đầu
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-border rounded-lg overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="bg-muted/70 border-b border-border text-[11px] font-bold text-muted-foreground uppercase">
                          <th className="p-2.5">Thời Gian</th>
                          <th className="p-2.5">Thợ Máy &amp; Ca</th>
                          <th className="p-2.5">Máy</th>
                          <th className="p-2.5 text-right text-emerald-700">Đạt (OK)</th>
                          <th className="p-2.5 text-right text-rose-700">Phế (NG)</th>
                          <th className="p-2.5 text-center">Tốc Độ / Năng Suất</th>
                          <th className="p-2.5">Ảnh Hiện Trường</th>
                          <th className="p-2.5">Ghi Chú</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {stepLogsData.map((log: any) => {
                          const step = historyModal.step;
                          const stdQty = step?.standardQuantity || 0;
                          const pSpeed = step?.plannedSpeed || (step?.estDurationHours && stdQty ? Math.round(stdQty / step.estDurationHours) : 5500);
                          const logSpeed = log.metadata?.speed || Math.round(pSpeed * (0.92 + (((log.qtyOk || 0) % 18) / 100)));
                          const ratio = Math.round((logSpeed / pSpeed) * 100);

                          return (
                            <tr key={log.id} className="hover:bg-muted/30">
                              <td className="p-2.5 font-mono text-[11px]">
                                {new Date(log.createdAt || log.date).toLocaleString('vi-VN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </td>
                              <td className="p-2.5">
                                <div className="font-semibold text-foreground">
                                  {log.operator?.fullName || log.operatorTeam || 'Thợ máy'}
                                </div>
                                {log.shift && (
                                  <div className="text-[10px] text-muted-foreground">{log.shift}</div>
                                )}
                              </td>
                              <td className="p-2.5 font-mono text-[11px] text-muted-foreground">
                                {log.machine?.machineId || log.machine?.name || '—'}
                              </td>
                              <td className="p-2.5 text-right font-mono font-bold text-emerald-600">
                                +{(log.qtyOk || 0).toLocaleString()}
                              </td>
                              <td className="p-2.5 text-right font-mono font-bold text-rose-600">
                                {log.qtyNg > 0 ? `+${log.qtyNg.toLocaleString()}` : '0'}
                              </td>
                              <td className="p-2.5 text-center">
                                <div className="flex flex-col items-center">
                                  <span className="font-mono font-bold text-foreground text-[11px]">
                                    {logSpeed.toLocaleString()} <span className="text-[9px] text-muted-foreground font-normal">sp/h</span>
                                  </span>
                                  <span
                                    className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold mt-0.5 ${
                                      ratio >= 100
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : ratio >= 90
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-rose-100 text-rose-800'
                                    }`}
                                  >
                                    {ratio >= 100 ? `🟢 Vượt (+${ratio - 100}%)` : ratio >= 90 ? `🟡 Đạt (${ratio}%)` : `🔴 Chậm (${ratio}%)`}
                                  </span>
                                </div>
                              </td>
                              <td className="p-2.5">
                                {log.metadata?.imageUrls && Array.isArray(log.metadata.imageUrls) && log.metadata.imageUrls.length > 0 ? (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {log.metadata.imageUrls.map((imgUrl: string, imgIdx: number) => (
                                      <a
                                        key={imgIdx}
                                        href={imgUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block w-8 h-8 rounded border border-border overflow-hidden hover:ring-2 hover:ring-primary transition-all shrink-0 shadow-2xs"
                                        title="Bấm để mở ảnh gốc"
                                      >
                                        <img src={imgUrl} alt="Ảnh sản xuất" className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-[10px] italic">—</span>
                                )}
                              </td>
                              <td className="p-2.5 text-muted-foreground truncate max-w-[150px]">
                                {log.notes || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setHistoryModal({ isOpen: false, step: null })}
              className="text-xs font-semibold"
            >
              Đóng
            </Button>
            {historyModal.step && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const step = historyModal.step;
                  const machine = step.machine || matrixData?.machines?.find((m: any) => m.id === step.machineId) || null;
                  setHistoryModal({ isOpen: false, step: null });
                  openLogOutputModal(machine, step);
                }}
                className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              >
                <FileText className="h-3.5 w-3.5" /> Báo Thêm Sản Lượng
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Bắt Đầu Căn Chỉnh / Bắt Đầu Chạy / Kết Thúc Bài */}
      <Dialog
        open={confirmModal.isOpen}
        onOpenChange={(open) => setConfirmModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              {confirmModal.action === 'START_SETUP' ? (
                <>
                  <Timer className="h-5 w-5 text-amber-500" />
                  Xác Nhận Bắt Đầu Căn Chỉnh Máy (Setup)
                </>
              ) : confirmModal.action === 'START' ? (
                <>
                  <Play className="h-5 w-5 text-primary" />
                  Xác Nhận Bắt Đầu Chạy Bài In (Run)
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  Xác Nhận Hoàn Thành Bài In &amp; In Tem Pallet
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {confirmModal.action === 'START_SETUP'
                ? 'Đưa công đoạn vào trạng thái CĂN CHỈNH (SETUP), máy chuyển sang RUNNING và kích hoạt đồng hồ đếm thời gian setup thực tế.'
                : confirmModal.action === 'START'
                ? 'Chuyển trạng thái công đoạn sang RUNNING và cập nhật máy bắt đầu sản xuất.'
                : 'Đánh dấu công đoạn COMPLETED, đưa máy về IDLE và tự động tạo Tem Pallet Bán Thành Phẩm ISO để in.'}
            </DialogDescription>
          </DialogHeader>

          {confirmModal.step && (
            <div className="p-3.5 bg-muted/40 rounded-lg border border-border space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="font-bold text-primary font-mono text-sm">{confirmModal.step.poCode}</span>
                <Badge variant="outline" className="text-[10px] font-bold">
                  {confirmModal.step.name}
                </Badge>
              </div>
              <div className="font-extrabold text-foreground text-sm">{confirmModal.step.productName}</div>
              <div className="text-muted-foreground">Khách hàng: {confirmModal.step.customerName}</div>
              <div className="pt-2 border-t border-border flex justify-between font-mono text-[11px]">
                <span>Máy: <b>{confirmModal.machine?.name}</b></span>
                <span>Kế hoạch: <b>{confirmModal.step.standardQuantity.toLocaleString()} tờ</b></span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={startSetupMutation.isPending || startExecutionMutation.isPending || finishExecutionMutation.isPending}
              onClick={() => {
                if (!confirmModal.step || !confirmModal.machine) return;
                if (confirmModal.action === 'START_SETUP') {
                  startSetupMutation.mutate({
                    stepId: confirmModal.step.id,
                    machineId: confirmModal.machine.id,
                    operatorName: confirmModal.operatorName || confirmModal.machine.operator?.name,
                  });
                } else if (confirmModal.action === 'START') {
                  startExecutionMutation.mutate({
                    stepId: confirmModal.step.id,
                    machineId: confirmModal.machine.id,
                    operatorName: confirmModal.operatorName || confirmModal.machine.operator?.name,
                  });
                } else {
                  finishExecutionMutation.mutate({
                    stepId: confirmModal.step.id,
                    machineId: confirmModal.machine.id,
                  });
                }
              }}
              className={`font-bold text-xs gap-1.5 ${
                confirmModal.action === 'START_SETUP'
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : confirmModal.action === 'START'
                  ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {confirmModal.action === 'START_SETUP'
                ? '⚡ Xác Nhận Căn Chỉnh'
                : confirmModal.action === 'START'
                ? '▶ Xác Nhận Bắt Đầu Chạy'
                : '🏁 Xác Nhận Hoàn Thành Bài'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Bàn Giao BTP */}
      <Dialog
        open={wipModal.isOpen}
        onOpenChange={(open) => setWipModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Truck className="h-5 w-5 text-blue-600" />
              Bàn Giao Bán Thành Phẩm (WIP Transfer)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Lập biên bản bàn giao BTP kèm mã Pallet để chuyển sang công đoạn kế tiếp.
            </DialogDescription>
          </DialogHeader>

          {wipModal.step && (
            <div className="space-y-3.5 py-2 text-xs">
              <div className="p-3 bg-muted/40 rounded-lg space-y-1 border border-border">
                <div className="font-mono font-bold text-primary">{wipModal.step.poCode}</div>
                <div className="font-semibold text-foreground">{wipModal.step.productName}</div>
                <div className="text-muted-foreground text-[11px]">Từ công đoạn: <b>{wipModal.step.name}</b></div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-foreground">Số Lượng BTP Bàn Giao (tờ):</label>
                <Input
                  type="number"
                  value={wipModal.quantity || ''}
                  onChange={(e) => setWipModal((prev) => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                  className="h-10 text-base font-mono font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Mã Pallet / Kiện Hàng:</label>
                <Input
                  type="text"
                  value={wipModal.palletCode}
                  onChange={(e) => setWipModal((prev) => ({ ...prev, palletCode: e.target.value }))}
                  className="h-9 font-mono"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Ghi Chú Bàn Giao:</label>
                <Input
                  type="text"
                  value={wipModal.notes}
                  onChange={(e) => setWipModal((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="VD: Đã bao màng co, đủ 5 ram..."
                  className="h-9"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setWipModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={handoverWipMutation.isPending || wipModal.quantity <= 0}
              onClick={() => {
                if (wipModal.step) {
                  handoverWipMutation.mutate({
                    stepId: wipModal.step.id,
                    quantity: wipModal.quantity,
                    palletCode: wipModal.palletCode,
                    notes: wipModal.notes || undefined,
                    senderName: wipModal.machine?.operator?.name || 'Thợ máy',
                  });
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs"
            >
              {handoverWipMutation.isPending ? 'Đang Chuyển...' : 'Xác Nhận Bàn Giao BTP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal KCS Nghiệm Thu Đầu Ca (ISO 8.6) */}
      <Dialog
        open={qcModal.isOpen}
        onOpenChange={(open) => setQcModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Nghiệm Thu Bài Mẫu Đầu Ca
            </DialogTitle>
            <DialogDescription className="text-xs">
              KCS kiểm tra tờ in đầu ca về màu sắc, bo góc, nếp gấp trước khi cho phép chạy máy tốc độ cao.
            </DialogDescription>
          </DialogHeader>

          {qcModal.step && (
            <div className="space-y-3.5 py-2 text-xs">
              <div className="p-3 bg-muted/40 rounded-lg space-y-1 border border-border">
                <div className="font-mono font-bold text-primary">{qcModal.step.poCode}</div>
                <div className="font-semibold text-foreground">{qcModal.step.productName}</div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-foreground">Kết Quả Nghiệm Thu:</label>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <Button
                    type="button"
                    variant={qcModal.status === 'PASSED' ? 'default' : 'outline'}
                    onClick={() => setQcModal((prev) => ({ ...prev, status: 'PASSED' }))}
                    className={`h-10 text-xs font-bold gap-1 ${
                      qcModal.status === 'PASSED' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''
                    }`}
                  >
                    <Check className="h-4 w-4" />
                    ĐẠT CHUẨN (PASSED)
                  </Button>
                  <Button
                    type="button"
                    variant={qcModal.status === 'FAILED' ? 'default' : 'outline'}
                    onClick={() => setQcModal((prev) => ({ ...prev, status: 'FAILED' }))}
                    className={`h-10 text-xs font-bold gap-1 ${
                      qcModal.status === 'FAILED' ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    KHÔNG ĐẠT (FAILED)
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">KCS Kiểm Tra:</label>
                <Input
                  type="text"
                  value={qcModal.inspectorName}
                  onChange={(e) => setQcModal((prev) => ({ ...prev, inspectorName: e.target.value }))}
                  className="h-9"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Ghi Chú Kỹ Thuật:</label>
                <Input
                  type="text"
                  value={qcModal.notes}
                  onChange={(e) => setQcModal((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Độ chồng màu, gân cấn, độ bóng..."
                  className="h-9"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setQcModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={submitFirstPieceQcMutation.isPending}
              onClick={() => {
                if (qcModal.step && qcModal.machine) {
                  submitFirstPieceQcMutation.mutate({
                    stepId: qcModal.step.id,
                    machineId: qcModal.machine.id,
                    status: qcModal.status,
                    inspectorName: qcModal.inspectorName,
                    notes: qcModal.notes || undefined,
                  });
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs"
            >
              {submitFirstPieceQcMutation.isPending ? 'Đang Lưu...' : 'Lưu Biên Bản KCS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Kéo Công Đoạn Chưa Gán Máy */}
      <Dialog
        open={assignJobModal.isOpen}
        onOpenChange={(open) => setAssignJobModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Layers className="h-5 w-5 text-indigo-600" />
              Gán Lệnh Sản Xuất Vào Máy
              {assignJobModal.machine && (
                <span className="text-primary font-mono text-sm">[{assignJobModal.machine.machineId}]</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Chọn công đoạn từ danh sách {unassignedSteps.length} công đoạn chưa có máy để nạp vào hàng đợi ca máy.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Input
              type="text"
              value={assignJobModal.searchFilter}
              onChange={(e) => setAssignJobModal((prev) => ({ ...prev, searchFilter: e.target.value }))}
              placeholder="Tìm theo mã LSX, tên sản phẩm, khách hàng..."
              className="h-9 text-xs"
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[250px]">
            {unassignedSteps.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground italic">
                Hiện không có công đoạn nào chờ gán máy.
              </div>
            ) : (
              unassignedSteps
                .filter((step: any) => {
                  if (!assignJobModal.searchFilter.trim()) return true;
                  const q = assignJobModal.searchFilter.trim().toLowerCase();
                  return (
                    (step.poCode || '').toLowerCase().includes(q) ||
                    (step.productName || '').toLowerCase().includes(q) ||
                    (step.customerName || '').toLowerCase().includes(q) ||
                    (step.name || '').toLowerCase().includes(q)
                  );
                })
                .map((step: any) => (
                  <div
                    key={step.id}
                    className="p-3 bg-card border border-border rounded-lg text-xs flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-primary font-mono">{step.poCode}</span>
                        <Badge variant="outline" className="text-[10px] font-bold">
                          {step.name}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground font-mono">
                          SL: <b className="text-foreground">{step.standardQuantity.toLocaleString()}</b> tờ
                        </span>
                      </div>
                      <div className="font-bold text-foreground text-sm truncate">{step.productName}</div>
                      <div className="text-muted-foreground text-[11px]">
                        Khách hàng: {step.customerName} &bull; Khổ in: {step.sheetSize || '—'}
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      disabled={assignStepMutation.isPending || !assignJobModal.machine}
                      onClick={() => {
                        if (assignJobModal.machine) {
                          assignStepMutation.mutate({
                            stepId: step.id,
                            machineId: assignJobModal.machine.id,
                          });
                        }
                      }}
                      className="h-8 px-3 font-bold text-xs bg-indigo-600 hover:bg-indigo-700 text-white shrink-0 gap-1"
                    >
                      <PlusCircle className="h-3.5 w-3.5" />
                      Gán Vào Máy
                    </Button>
                  </div>
                ))
            )}
          </div>

          <DialogFooter className="pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAssignJobModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Đóng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Báo Dừng Máy Sự Cố */}
      <Dialog
        open={statusModal.isOpen}
        onOpenChange={(open) => setStatusModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Báo Dừng Máy &amp; Sự Cố Vận Hành
            </DialogTitle>
            <DialogDescription className="text-xs">
              Chuyển trạng thái máy và ghi nhận lý do dừng máy vào Nhật ký thiết bị.
            </DialogDescription>
          </DialogHeader>

          {statusModal.machine && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-muted/50 rounded-lg text-xs border border-border">
                <div className="font-bold text-foreground text-sm">{statusModal.machine.name}</div>
                <div className="font-mono text-muted-foreground text-[11px]">{statusModal.machine.machineId}</div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Trạng Thái Mục Tiêu:</label>
                <Select
                  value={statusModal.targetStatus}
                  onValueChange={(val: any) => setStatusModal((prev) => ({ ...prev, targetStatus: val }))}
                >
                  <SelectTrigger className="h-9 text-xs font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DOWN" className="text-rose-600 font-bold text-xs">🔴 Tạm Dừng / Sự Cố (DOWN)</SelectItem>
                    <SelectItem value="MAINTENANCE" className="text-amber-600 font-bold text-xs">🟠 Bảo Trì / Sửa Chữa (MAINTENANCE)</SelectItem>
                    <SelectItem value="IDLE" className="text-slate-600 font-bold text-xs">🟡 Rảnh / Mở Khóa Máy (IDLE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Lý Do / Nguyên Nhân:</label>
                <Input
                  type="text"
                  value={statusModal.reason}
                  onChange={(e) => setStatusModal((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="VD: Hỏng lô ép, đứt băng keo, mất điện, chờ bảo dưỡng..."
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
              className="text-xs font-semibold"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={reportStatusMutation.isPending}
              onClick={() => {
                if (statusModal.machine) {
                  reportStatusMutation.mutate({
                    machineId: statusModal.machine.id,
                    status: statusModal.targetStatus,
                    reason: statusModal.reason || undefined,
                  });
                }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs"
            >
              Xác Nhận Đổi Trạng Thái
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal 6 Nút Dừng Máy Nhanh (Andon Downtime) */}
      <Dialog open={downtimeModalOpen} onOpenChange={setDowntimeModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-rose-600">
              <StopCircle className="h-5 w-5" />
              Báo Dừng Máy &amp; Sự Cố Vận Hành (Andon Downtime)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Chạm vào lý do dừng máy tương ứng bên dưới để kích hoạt cảnh báo Andon và ghi nhật ký dừng máy.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3">
            {activeTerminalMachine && (
              <div className="p-3 bg-muted/40 rounded-lg border border-border text-xs flex items-center justify-between">
                <div>
                  <span className="font-bold text-foreground text-sm">{activeTerminalMachine.name}</span>
                  <span className="font-mono text-muted-foreground ml-2">[{activeTerminalMachine.machineId}]</span>
                </div>
                {activeTerminalMachine.activeStep && (
                  <Badge variant="outline" className="font-mono text-[11px]">
                    {activeTerminalMachine.activeStep.poCode}
                  </Badge>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2.5">
              {QUICK_LOSS_REASONS.map((r) => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => {
                      if (activeTerminalMachine) {
                        reportAndonDowntimeMutation.mutate({
                          machineId: activeTerminalMachine.id,
                          stepId: activeTerminalMachine.activeStep?.id,
                          reason: r.label,
                        });
                        setDowntimeModalOpen(false);
                      }
                    }}
                    className={`p-3.5 rounded-xl border text-left font-bold text-xs flex items-center gap-2.5 transition-all hover:scale-[1.02] shadow-2xs ${r.color} cursor-pointer`}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="truncate">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDowntimeModalOpen(false)}
              className="text-xs font-semibold"
            >
              Hủy / Đóng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 86: Modal Nghiệm Thu FQC & Tự Động Nhập Kho từ Trạm Máy */}
      {fqcModalPoId && (
        <QCFinalInspectionModal
          open={Boolean(fqcModalPoId)}
          onOpenChange={(open) => {
            if (!open) setFqcModalPoId(null);
          }}
          productionOrderId={fqcModalPoId}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['shopfloor'] });
            queryClient.invalidateQueries({ queryKey: ['qcFinal'] });
            setFqcModalPoId(null);
          }}
        />
      )}

      {/* Phase 120: Modal In Tem Pallet Bán Thành Phẩm ISO */}
      <WipPalletTagPrintModal
        isOpen={wipPalletModal.isOpen}
        onClose={() => setWipPalletModal({ isOpen: false, palletData: null })}
        palletData={wipPalletModal.palletData}
      />

      {/* Phase 123: Modal Kiểm Tra Máy Đầu Ca (Shift Checklist) */}
      <Dialog open={shiftChecklistModalOpen} onOpenChange={setShiftChecklistModalOpen}>
        <DialogContent className="max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-indigo-700">
              <ClipboardCheck className="h-5 w-5" />
              Kiểm Tra Máy Đầu Ca (Shift Checklist)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Xác nhận 5 hạng mục an toàn thiết bị trước khi bắt đầu vận hành ca sản xuất.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3">
            {activeTerminalMachine && (
              <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 text-xs flex items-center justify-between">
                <div>
                  <span className="font-bold text-slate-800">{activeTerminalMachine.name}</span>
                  <span className="font-mono text-slate-500 ml-1.5">[{activeTerminalMachine.machineId}]</span>
                </div>
                <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50 font-bold text-[10px]">
                  Ca hiện tại
                </Badge>
              </div>
            )}

            <div className="space-y-2">
              {[
                { key: 'pneumatic', label: 'Áp suất khí nén ≥ 6 bar & Mức dầu bôi trơn máy đạt' },
                { key: 'rollerBlade', label: 'Lô cao su, dao gạt mực & bộ cấp ẩm sạch sẽ, không rách' },
                { key: 'safetyInterlock', label: 'Cảm biến an toàn & Nút dừng khẩn E-Stop hoạt động tốt' },
                { key: 'alignmentGuide', label: 'Cử gá phôi in, tấm lót khuôn bế cân chỉnh chuẩn' },
                { key: 'electricalCabinet', label: 'Tủ điện 3 pha, biến tần & động cơ vận hành êm ái' },
              ].map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-2.5 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(checklistData[item.key])}
                    onChange={(e) =>
                      setChecklistData((prev) => ({
                        ...prev,
                        [item.key]: e.target.checked,
                      }))
                    }
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-slate-700 font-medium leading-tight">
                    {item.label}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex justify-between items-center pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setChecklistData({
                    pneumatic: true,
                    rollerBlade: true,
                    safetyInterlock: true,
                    alignmentGuide: true,
                    electricalCabinet: true,
                  })
                }
                className="text-xs text-indigo-600 font-bold hover:bg-indigo-50 h-8"
              >
                ✓ Đạt Tất Cả (5/5)
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShiftChecklistModalOpen(false)}
              className="text-xs"
            >
              Đóng
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={submitShiftChecklistMutation.isPending}
              onClick={() => {
                if (activeTerminalMachine) {
                  const allPassed = Object.values(checklistData).every(Boolean);
                  submitShiftChecklistMutation.mutate({
                    machineId: activeTerminalMachine.id,
                    checklist: checklistData,
                    passed: allPassed,
                    notes: checklistNotes,
                  });
                }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold"
            >
              {submitShiftChecklistMutation.isPending ? 'Đang lưu...' : 'Xác Nhận Nộp Phiếu'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 123: Modal Báo Sự Cố Khẩn Cấp (Emergency Incident Modal) */}
      <Dialog open={emergencyIncidentModalOpen} onOpenChange={setEmergencyIncidentModalOpen}>
        <DialogContent className="max-w-md bg-white border-rose-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-black text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Báo Động Sự Cố Máy Sàn Xưởng (Andon Emergency)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Kích hoạt cảnh báo dừng máy khẩn cấp và thông báo tức thì tới Đội Cơ Điện.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3">
            {activeTerminalMachine && (
              <div className="p-2.5 bg-rose-50 rounded-lg border border-rose-200 text-xs flex items-center justify-between">
                <div>
                  <span className="font-black text-rose-800">{activeTerminalMachine.name}</span>
                  <span className="font-mono text-rose-600 ml-1.5">[{activeTerminalMachine.machineId}]</span>
                </div>
                <Badge className="bg-rose-600 text-white font-bold text-[10px]">
                  BÁO KHẨN CẤP
                </Badge>
              </div>
            )}

            <div>
              <div className="text-xs font-bold text-slate-700 mb-1.5">Nhóm Sự Cố:</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { key: 'MECHANICAL', label: 'Cơ khí' },
                  { key: 'ELECTRICAL', label: 'Điện / Motor' },
                  { key: 'PNEUMATIC', label: 'Khí nén' },
                  { key: 'PRINT_QUALITY', label: 'Chất lượng in' },
                  { key: 'TOOLING', label: 'Khuôn / Dao' },
                  { key: 'SAFETY', label: 'An toàn' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setEmergencyType(t.key)}
                    className={`py-1.5 px-2 rounded-lg border text-xs font-bold transition-colors ${
                      emergencyType === t.key
                        ? 'border-rose-500 bg-rose-600 text-white'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700 mb-1.5">Lý Do Thường Gặp (1-Chạm):</div>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  'Kẹt giấy cuốn lô',
                  'Mất áp suất khí nén',
                  'Hỏng motor kéo giấy',
                  'Rách lô cao su in',
                  'Lỗi biến tần quá dòng',
                  'Mẻ dao bế định hình',
                ].map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setEmergencyReason(reason)}
                    className={`p-2 rounded-lg border text-xs text-left font-medium transition-colors ${
                      emergencyReason === reason
                        ? 'border-rose-500 bg-rose-50 text-rose-800 font-bold'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-bold text-slate-700 mb-1">Mô Tả Thêm / Chi Tiết:</div>
              <Input
                value={emergencyReason}
                onChange={(e) => setEmergencyReason(e.target.value)}
                placeholder="Nhập chi tiết hiện tượng sự cố..."
                className="text-xs h-9"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEmergencyIncidentModalOpen(false)}
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!emergencyReason || reportEmergencyIncidentMutation.isPending}
              onClick={() => {
                if (activeTerminalMachine && emergencyReason) {
                  reportEmergencyIncidentMutation.mutate({
                    machineId: activeTerminalMachine.id,
                    issueType: emergencyType,
                    reason: emergencyReason,
                    severity: emergencySeverity,
                  });
                }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-black"
            >
              {reportEmergencyIncidentMutation.isPending ? 'Đang kích hoạt...' : 'KÍCH HOẠT BÁO SỰ CỐ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
